import type { UserSourcesAPIOptions } from '@api/Repositories/User/UserSourcesRepository';
import type { SelectMap } from '../../MedicareProFront/Utilities/SelectMap';
import type { AutomationPayload } from '../Entities/Automation/Automation';
import type {
  CommissionStatementModuleMode,
  CommissionStatementModuleName
} from '../Model/CommissionStatement/CommissionStatementModule';
import type { CalendarEventPayload } from '../Repositories/CalendarEventRepository';
import type { CommissionStatementMappingCompanyPayload } from '../Repositories/CommissionStatement/CommissionStatementMappingCompanyRepository';
import type {
  CommissionStatementMappingCollectionPayload,
  CommissionStatementMappingPayload
} from '../Repositories/CommissionStatement/CommissionStatementMappingRepository';
import type {
  CommissionStatementCollectionPayload,
  CommissionStatementPayload
} from '../Repositories/CommissionStatement/CommissionStatementRepository';
import type {
  CommissionStatementResultAPIOptions,
  CommissionStatementResultCollectionPayload,
  CommissionStatementResultPayload
} from '../Repositories/CommissionStatement/CommissionStatementResultRepository';
import type { DeleteResponse } from '../Repositories/CoreRepository';
import type { UserIntegrationFeatureAvailabilityResponse } from '../Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import type {
  UserMetaCollectionPayload,
  UserMetaPayload,
  UserMetaPayloadSimple
} from '../Repositories/User/UserMetaRepository';
import type {
  oAuthGetTokenPayload,
  oAuthGetTokenResponse,
  oAuthLoginPayload,
  oAuthLoginResponse,
  oAuthRefreshTokenPayload,
  oAuthRefreshTokenResponse
} from '../Repositories/User/UserTokenRepository';
import { CoreAPI } from './CoreAPI';
/**
 * Response interface for file upload operations
 */
export interface FileUploadResponse {
  /** The name of the uploaded file */
  fileName: string;
  /** Unique key identifier for the uploaded file */
  key: string;
  /** Display name of the file */
  name: string;
}
/**
 * Type definition for spreadsheet metadata containing sheet information
 */
export type SpreadsheetMetadata = {
  /** Array of available sheets in the spreadsheet */
  sheets: Array<SelectMap>;
};
/**
 * Web API Class
 *
 * Primary API client for communicating with the MedicareProCRM backend services.
 * Extends CoreAPI to provide comprehensive CRUD operations for individuals, policies,
 * commissions, integrations, and various other CRM entities.
 *
 * This class handles:
 * - Ind management (CRUD operations, address, phone, email management)
 * - Policy and commission statement processing
 * - Third-party integrations (VoIP providers, email services, calendar systems)
 * - File uploads and document management
 * - Automation and workflow management
 * - Reporting and export functionality
 *
 * @extends CoreAPI
 * @author MedicarePro Development Team
 */
export class WebAPI extends CoreAPI {
  /**
   * Get synchronized applications for individuals
   *
   * Retrieves a list of applications that are currently synchronized with
   * the individual records in the system.
   *
   * @returns Promise containing the list of synchronized applications
   */
  public getIndSynchedApps() {
    return this.sendRequest('/individuals/synched-apps');
  }

  /**
   * Get a specific individual by ID
   *
   * Retrieves detailed information about a specific individual using their unique ID.
   *
   * @param individualId - The unique identifier of the individual
   * @param options - Additional query parameters for the request
   * @returns Promise containing the individual's data
   */
  public getInd(individualId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${individualId}`, options);
  }

  /**
   * Get an individual by phone number
   *
   * Searches for and retrieves an individual's information using their phone number.
   * Useful for caller ID lookups and quick contact identification.
   *
   * @param phone - The phone number to search for
   * @param options - Additional search options and query parameters
   * @returns Promise containing the individual's data
   */
  public getIndByNumber(phone: string, options: any = {}) {
    options.phone = phone;
    return this.sendRequest('/individuals/by-phone', options);
  }

  /**
   * Create a new individual record
   *
   * Creates a new individual in the system under a specific user's account.
   *
   * @param userId - The ID of the user who will own this individual record
   * @param payload - The individual data to create
   * @returns Promise containing the created individual's data
   */
  public createInd(userId: number, payload: object = {}) {
    return this.sendRequest(`/user/${userId}/individual`, null, { method: 'POST', body: payload });
  }

  /**
   * Update an existing individual's information
   *
   * Applies partial updates to an individual's record using PATCH methodology.
   *
   * @param individualId - The ID of the individual to update
   * @param payload - Array of update operations to apply
   * @returns Promise containing the updated individual's data
   */
  public updateInd(individualId: number, payload: any) {
    return this.sendRequest(`/individuals/${individualId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete an individual record
   *
   * Permanently removes an individual from the system. This action cannot be undone.
   *
   * @param individualId - The ID of the individual to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteInd(individualId: number[]) {
    return this.sendRequest(`/individuals/${individualId}`, null, {
      method: 'DELETE',
      body: individualId,
    });
  }

  /**
   * Get a collection of individuals
   *
   * Retrieves a paginated list of individuals with optional filtering and sorting.
   *
   * @param options - Query parameters for filtering, sorting, and pagination
   * @returns Promise containing the collection of individuals
   */
  public getInds(options: object = {}) {
    return this.sendRequest('/individuals', options);
  }

  /**
   * Get total individuals
   *
   * Retrieves the total number of idividuals belonging to this user
   *
   * @param options - Query parameters for filtering
   * @returns Promise containing the total number of individuals
   */
  public getIndsTotal(options: object = {}) {
    return this.sendRequest('/individuals/total', options);
  }

  /**
   * Search for healthcare providers in the NPPES database
   *
   * Queries the National Plan and Provider Enumeration System (NPPES) to find
   * healthcare providers based on search criteria.
   *
   * @param criteria - Search criteria for finding providers
   * @returns Promise containing the list of matching providers
   */
  public getNppesProviders(criteria: object = {}) {
    const options = {};
    if (criteria) options['criteria'] = criteria;
    console.log(options);
    return this.sendRequest('/integrations/nppes/provider-search', options);
  }

  /**
   * Get individuals grouped by a specific field
   *
   * Retrieves individuals organized into groups based on a specified field value.
   * Useful for creating summary reports and analytics.
   *
   * @param fieldName - The field to group individuals by
   * @param ownerId - The ID of the user who owns the individuals
   * @param criteria - Additional filtering criteria
   * @returns Promise containing the grouped individual data
   */
  public getIndsGrouped(fieldName: string, ownerId: number, criteria: object = {}) {
    const options = {};
    if (fieldName) options['field'] = fieldName;
    if (ownerId) options['ownerId'] = ownerId;
    if (criteria) options['criteria'] = criteria;
    return this.sendRequest('/individuals/grouped', options);
  }

  /**
   * Bulk update multiple individuals
   *
   * Applies updates to multiple individual records in a single operation.
   * More efficient than updating individuals one by one.
   *
   * @param userId - The ID of the user performing the updates
   * @param payload - Array of update operations to apply
   * @returns Promise containing the update results
   */
  public updateInds(userId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/individuals`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Generate a URL for exporting individuals
   *
   * Creates a download URL for exporting individual data in various formats.
   * The URL includes authentication tokens for secure access.
   *
   * @param options - Export options including format and filtering
   * @returns String URL for downloading the export
   */
  public exportIndsUrl(options: object = {}) {
    if (this.accessToken) {
      options['accessToken'] = this.accessToken;
    }
    return `${this.baseUrl}/individuals/export?${this.buildQuery(options)}`;
  }

  /**
   * Start an individuals export process
   *
   * Initiates an asynchronous export job for individual data. Use this for large
   * exports that may take time to process.
   *
   * @param options - Export configuration options
   * @param excludeIds - Array of individual IDs to exclude from the export
   * @returns Promise containing the export job details
   */
  public exportIndsStart(options: object = {}, excludeIds: Array<number> = null) {
    const parameters: any = { method: 'POST' };
    if (excludeIds) {
      parameters.body = {
        excludeIds: excludeIds,
      };
    }
    return this.sendRequest('/individuals/exports', options, parameters);
  }

  /**
   * Process an individuals export
   *
   * Continues processing an export job that was started with exportIndsStart.
   *
   * @param indExportId - The ID of the export job to process
   * @param options - Additional processing options
   * @returns Promise containing the processing status
   */
  public exportIndsProcess(indExportId: number, options: object = {}) {
    return this.sendRequest(`/individuals/exports/${indExportId}/process`, options, {
      method: 'POST',
    });
  }

  /**
   * Download a completed individuals export
   *
   * Downloads the final export file once processing is complete.
   *
   * @param indExportId - The ID of the completed export job
   * @param options - Download options
   * @returns Promise containing the download response
   */
  public exportIndsDownload(indExportId: number, options: object = {}) {
    return this.sendRequest(`/individuals/exports/${indExportId}/download`, options, {
      method: 'POST',
    });
  }

  /**
   * Bulk delete multiple individuals
   *
   * Permanently removes multiple individuals from a user's account in a single operation.
   *
   * @param userId - The ID of the user performing the deletion
   * @param individualIds - Array of individual IDs to delete
   * @returns Promise containing the deletion results
   */
  public deleteInds(userId: number, individualIds: number[]) {
    return this.sendRequest(`/users/${userId}/individuals`, null, {
      method: 'DELETE',
      body: individualIds,
    });
  }

  /**
   * Remove flags from multiple individuals
   *
   * Batch operation to remove specific flags from multiple individual records.
   *
   * @param options - Configuration specifying which flags to remove and from which individuals
   * @returns Promise containing the operation results
   */
  public deleteIndsFlag(options: object = {}) {
    return this.sendRequest('/individuals/flags/batch', null, { method: 'DELETE', body: options });
  }

  /**
   * Add flags to multiple individuals
   *
   * Batch operation to add specific flags to multiple individual records.
   * Useful for marking individuals for follow-up, categorization, etc.
   *
   * @param options - Configuration specifying which flags to add and to which individuals
   * @returns Promise containing the operation results
   */
  public addIndsFlag(options: object = {}) {
    return this.sendRequest('/individuals/flags/batch', null, { method: 'POST', body: options });
  }

  /**
   * Get agency integration OAuth configuration
   *
   * Retrieves OAuth settings and tokens for an agency's integration with a third-party provider.
   *
   * @param agencyId - The ID of the agency
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Additional query parameters
   * @returns Promise containing the OAuth configuration
   */
  public getAgencyIntegrationOAuth(
    agencyId: number,
    providerReference: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/agencies/${agencyId}/integrations/${providerReference}/oauth`,
      options
    );
  }

  /**
   * Get VoIP phone numbers for an agency integration
   *
   * Retrieves available phone numbers from a VoIP provider integration for an agency.
   *
   * @param agencyId - The ID of the agency
   * @param providerReference - The VoIP provider reference
   * @param accountId - The account ID with the VoIP provider
   * @returns Promise containing the list of available phone numbers
   */
  public getAgencyIntegrationOAuthVoipPhoneNumbers(
    agencyId: number,
    providerReference: string,
    accountId: number
  ) {
    return this.sendRequest(
      `/agencies/${agencyId}/integrations/oauth/${providerReference}/${accountId}/phone-numbers`
    );
  }

  /**
  * Get VoIP phone numbers for a user PAT integration
  * 
  * Retrieves available phone numbers from a VoIP provider integration for a user.
  * 
  * @param userId - The ID of the user
  * @param providerReference - The VoIP provider reference
  * @returns Promise containing the list of available phone numbers
  */
  public getUserIntegrationPatVoipPhoneNumbers(userId: number, providerReference: string) {
    return this.sendRequest('/users/' + userId + '/integrations/pat/' + providerReference + '/phone-numbers');
  }

  /**
   * Update agency integration OAuth configuration
   *
   * Updates the OAuth settings and credentials for an agency's third-party integration.
   *
   * @param agencyId - The ID of the agency
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Query parameters for the update
   * @param payload - The updated OAuth configuration data
   * @returns Promise containing the updated configuration
   */
  public putAgencyIntegrationOAuth(
    agencyId: number,
    providerReference: string,
    options: object = {},
    payload: object = {}
  ) {
    return this.sendRequest(
      `/agencies/${agencyId}/integrations/${providerReference}/oauth`,
      options,
      { method: 'PUT', body: payload }
    );
  }

  /**
   * Deauthorize an agency's integration OAuth access
   *
   * Revokes OAuth authorization for an agency's integration with a third-party provider.
   * This will disable the integration until re-authorized.
   *
   * @param agencyId - The ID of the agency
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Additional options for the deauthorization
   * @returns Promise containing the deauthorization confirmation
   */
  public deauthorizeAgencyIntegrationOAuth(
    agencyId: number,
    providerReference: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/agencies/${agencyId}/integrations/${providerReference}/oauth/deauthorize`,
      options,
      { method: 'POST' }
    );
  }

  /**
   * Get user integration OAuth configuration
   *
   * Retrieves OAuth settings and tokens for a user's integration with a third-party provider.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Additional query parameters
   * @returns Promise containing the OAuth configuration
   */
  public getUserIntegrationOAuth(userId: number, providerReference: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/integrations/${providerReference}/oauth`, options);
  }

  /**
   * Get automation actions for a specific automation
   *
   * Retrieves all actions configured for a particular automation workflow.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation
   * @param options - Additional query parameters
   * @returns Promise containing the list of automation actions
   */
  public getAutomationActions(userId: number, automationId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/automations/${automationId}/actions`, options);
  }

  /**
   * Get a specific automation action
   *
   * Retrieves details for a single automation action by its ID.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation
   * @param automationActionId - The ID of the specific action
   * @param options - Additional query parameters
   * @returns Promise containing the automation action details
   */
  public getAutomationAction(
    userId: number,
    automationId: number,
    automationActionId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/automations/${automationId}/actions/${automationActionId}`,
      options
    );
  }

  /**
   * Delete an automation action
   *
   * Permanently removes an action from an automation workflow.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation
   * @param automationActionId - The ID of the action to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteAutomationAction(userId: number, automationId: number, automationActionId: number) {
    return this.sendRequest(
      `/users/${userId}/automations/${automationId}/actions/${automationActionId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Update an automation action
   *
   * Modifies the configuration of an existing automation action.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation
   * @param automationActionId - The ID of the action to update
   * @param payload - The updated action configuration
   * @returns Promise containing the updated action details
   */
  public updateAutomationAction(
    userId: number,
    automationId: number,
    automationActionId: number,
    payload: object
  ) {
    return this.sendRequest(
      `/users/${userId}/automations/${automationId}/actions/${automationActionId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Create a new automation action
   *
   * Adds a new action to an existing automation workflow.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation
   * @param payload - The action configuration data
   * @returns Promise containing the created action details
   */
  public createAutomationAction(userId: number, automationId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/automations/${automationId}/actions`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get automation schedules for a user
   *
   * Retrieves all scheduled automation runs for a specific user.
   *
   * @param userId - The ID of the user
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the list of automation schedules
   */
  public getAutomationSchedules(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/schedules`, options);
  }

  /**
   * Get a specific automation schedule
   *
   * Retrieves details for a single automation schedule by its ID.
   *
   * @param userId - The ID of the user who owns the schedule
   * @param scheduleId - The ID of the schedule
   * @param options - Additional query parameters
   * @returns Promise containing the schedule details
   */
  public getAutomationSchedule(userId: number, scheduleId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/schedules/${scheduleId}`, options);
  }

  /**
   * Delete an automation schedule
   *
   * Permanently removes a scheduled automation run.
   *
   * @param userId - The ID of the user who owns the schedule
   * @param scheduleId - The ID of the schedule to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteAutomationSchedule(userId: number, scheduleId: number) {
    return this.sendRequest(`/users/${userId}/schedules/${scheduleId}`, null, { method: 'DELETE' });
  }

  /**
   * Update an automation schedule
   *
   * Modifies the timing and configuration of an existing automation schedule.
   *
   * @param userId - The ID of the user who owns the schedule
   * @param scheduleId - The ID of the schedule to update
   * @param payload - The updated schedule configuration
   * @returns Promise containing the updated schedule details
   */
  public updateAutomationSchedule(userId: number, scheduleId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/schedules/${scheduleId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create a new automation schedule
   *
   * Sets up a new scheduled run for an automation workflow.
   *
   * @param userId - The ID of the user who will own the schedule
   * @param payload - The schedule configuration data
   * @returns Promise containing the created schedule details
   */
  public createAutomationSchedule(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/schedules`, null, { method: 'POST', body: payload });
  }

  /**
   * Get automations for a user
   *
   * Retrieves all automation workflows owned by a specific user.
   *
   * @param userId - The ID of the user
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the list of automations
   */
  public getAutomations(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/automations`, options);
  }

  /**
   * Get automations for an agency
   *
   * Retrieves all automation workflows available to or owned by an agency.
   *
   * @param agencyId - The ID of the agency
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the list of agency automations
   */
  public getAgencyAutomations(agencyId: number, options: object = {}) {
    return this.sendRequest(`/agencies/${agencyId}/automations`, options);
  }

  /**
   * Get calendar events between specific dates
   *
   * Retrieves calendar events for a user within a specified date range.
   * Useful for calendar views and scheduling displays.
   *
   * @param userId - The ID of the user whose calendar events to retrieve
   * @param dateTimeStart - The start date/time for the range (ISO format)
   * @param dateTimeEnd - The end date/time for the range (ISO format)
   * @param options - Additional query parameters
   * @returns Promise containing the calendar events within the date range
   */
  public getCalendarEventsBetweenDates(
    userId: number,
    dateTimeStart: string,
    dateTimeEnd: string,
    options: {} = null
  ) {
    const optionsFinal = { ...options, dateTimeStart: dateTimeStart, dateTimeEnd: dateTimeEnd };
    return this.sendRequest(`/users/${userId}/calendar-events/between-dates`, optionsFinal);
  }

  /**
   * Get agency downline calendar events between dates
   *
   * Retrieves calendar events for all users in an agency's downline within a specified date range.
   * Useful for agency managers to see all team activities.
   *
   * @param userId - The ID of the user requesting the data
   * @param agencyId - The ID of the agency
   * @param dateTimeStart - The start date/time for the range (ISO format)
   * @param dateTimeEnd - The end date/time for the range (ISO format)
   * @param options - Additional query parameters
   * @returns Promise containing the downline calendar events within the date range
   */
  public getAgencyDownlineCalendarEventsBetweenDates(
    userId: number,
    agencyId: number,
    dateTimeStart: string,
    dateTimeEnd: string,
    options: {} = null
  ) {
    const optionsFinal = { ...options, dateTimeStart: dateTimeStart, dateTimeEnd: dateTimeEnd };
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/downlines/calendar-events/between-dates`,
      optionsFinal
    );
  }

  /**
   * Get calendar events for a user
   *
   * Retrieves all calendar events for a specific user with optional filtering.
   *
   * @param userId - The ID of the user whose calendar events to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's calendar events
   */
  public getCalendarEvents(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/calendar-events`, options);
  }

  /**
   * Get a specific calendar event
   *
   * Retrieves detailed information for a single calendar event by its ID.
   *
   * @param userId - The ID of the user who owns the calendar event
   * @param calendarEventId - The ID of the calendar event
   * @param options - Additional query parameters
   * @returns Promise containing the calendar event details
   */
  public getCalendarEvent(userId: number, calendarEventId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/calendar-events/${calendarEventId}`, options);
  }

  /**
   * Delete a calendar event
   *
   * Permanently removes a calendar event from the user's calendar.
   *
   * @param userId - The ID of the user who owns the calendar event
   * @param calendarEventId - The ID of the calendar event to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteCalendarEvent(userId: number, calendarEventId: number) {
    return this.sendRequest(`/users/${userId}/calendar-events/${calendarEventId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Update a calendar event
   *
   * Modifies the details of an existing calendar event.
   *
   * @param userId - The ID of the user who owns the calendar event
   * @param calendarEventId - The ID of the calendar event to update
   * @param payload - The updated calendar event data
   * @returns Promise containing the updated calendar event details
   */
  public updateCalendarEvent(
    userId: number,
    calendarEventId: number,
    payload: CalendarEventPayload
  ) {
    return this.sendRequest(`/users/${userId}/calendar-events/${calendarEventId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create a new calendar event
   *
   * Adds a new event to the user's calendar.
   *
   * @param userId - The ID of the user who will own the calendar event
   * @param payload - The calendar event data to create
   * @returns Promise containing the created calendar event details
   */
  public createCalendarEvent(userId: number, payload: CalendarEventPayload) {
    return this.sendRequest(`/users/${userId}/calendar-events`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get an external calendar event
   *
   * Retrieves a calendar event that was created by an external system using a vendor ID.
   *
   * @param userId - The ID of the user who owns the calendar event
   * @param vendorID - The external vendor's ID for the calendar event
   * @param options - Additional query parameters
   * @returns Promise containing the external calendar event details
   */
  public getCalendarEventExternal(userId: number, vendorID: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/calendar-events/external/${vendorID}`, options);
  }

  /**
   * Delete an external calendar event
   *
   * Removes a calendar event that was created by an external system.
   *
   * @param userId - The ID of the user who owns the calendar event
   * @param vendorID - The external vendor's ID for the calendar event
   * @returns Promise containing the deletion confirmation
   */
  public deleteCalendarEventExternal(userId: number, vendorID: string) {
    return this.sendRequest(`/users/${userId}/calendar-events/external/${vendorID}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create or update an external calendar event
   *
   * Creates a new external calendar event or updates an existing one using PUT method.
   *
   * @param userId - The ID of the user who will own the calendar event
   * @param vendorID - The external vendor's ID for the calendar event
   * @param payload - The calendar event data
   * @returns Promise containing the calendar event details
   */
  public putCalendarEventExternal(userId: number, vendorID: string, payload: CalendarEventPayload) {
    return this.sendRequest(`/users/${userId}/calendar-events/external/${vendorID}`, null, {
      method: 'PUT',
      body: payload,
    });
  }

  /**
   * Get a particular user's meta data
   *
   * @param userId - User whos meta data you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of UserMetaCollectionPayload
   */
  public getUserMetaList(userId: number, options: object = {}): Promise<UserMetaCollectionPayload> {
    return this.sendRequest(`/users/${userId}/meta`, options);
  }

  /**
   * Get a single piece of for a particular user
   *
   * @param userMetaKey - Id of the meta data you'd like to get
   * @param userId - User whos meta data you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of UserMetaPayload
   */
  public getUserMeta(
    userId: number,
    userMetaKey: string,
    options: object = {}
  ): Promise<UserMetaPayload> {
    return this.sendRequest(`/users/${userId}/meta/${userMetaKey}`, options);
  }

  /**
   * Update a meta data
   *
   * @param userId - User whos meta data you want to update
   * @param payload - Object containig the updates values for the Meta
   * @returns Promise containing an instance of UserMetaPayload
   */
  public updateUserMeta(userId: number, payload: UserMetaPayloadSimple): Promise<UserMetaPayload> {
    return this.sendRequest(`/users/${userId}/meta`, null, { method: 'POST', body: payload });
  }

  /**
   * Update a meta data
   *
   * @param userMetaKey - Key of the meta data you want to delete
   * @param userId - User whos meta data you want to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserMeta(userId: number, userMetaKey: string): Promise<DeleteResponse> {
    return this.sendRequest(`/users/${userId}/meta/${userMetaKey}`, null, { method: 'DELETE' });
  }

  /**
   * Get a particular user's Commission Statements
   *
   * @param userId - User whos commission statements you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of CommissionStatementCollectionPayload
   */
  public getCommissionStatements(
    userId: number,
    options: object = {}
  ): Promise<CommissionStatementCollectionPayload> {
    return this.sendRequest(`/users/${userId}/commission-statements`, options);
  }

  /**
   * Get a particular user's Commission Statement results
   *
   * @param userId - User whos commission statements you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of CommissionStatementResultCollectionPayload
   */
  public getCommissionStatementsResults(
    userId: number,
    options: object = {}
  ): Promise<CommissionStatementResultCollectionPayload> {
    return this.sendRequest(`/users/${userId}/commission-statements/results`, options);
  }

  /**
   * Get a single Commission Statement for a particular user
   *
   * @param commissionStatementId - Id of the commission statement you'd like to get
   * @param userId - User whos commission statements you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of CommissionStatementPayload
   */
  public getCommissionStatement(
    userId: number,
    commissionStatementId: number,
    options: object = {}
  ): Promise<CommissionStatementPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}`,
      options
    );
  }

  /**
   * Update a commission statement
   *
   * @param commissionStatementId - ID of the commission statement you want to update
   * @param userId - User whos commission statement you want to update
   * @param payload - Object containig the updates values for the Commission Statement
   * @returns Promise containing an instance of CommissionStatementPayload
   */
  public updateCommissionStatement(
    userId: number,
    commissionStatementId: number,
    payload: CommissionStatementPayload
  ): Promise<CommissionStatementPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Create a commission statement
   *
   * @param userId - User whos will own this commission statement
   * @param payload - Object containig the updates values for the Commission Statement
   * @returns Promise containing an instance of CommissionStatementPayload
   */
  public createCommissionStatement(
    userId: number,
    payload: CommissionStatementPayload
  ): Promise<CommissionStatementPayload> {
    return this.sendRequest(`/users/${userId}/commission-statements`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Process a commission statement data file
   *
   * @param commissionStatementId - ID of the commission statement you want to process
   * @param userId - User whos commission statement you want to process
   * @returns Promise containing an instance of CommissionStatementPayload
   */
  public processCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<CommissionStatementPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/process`,
      null,
      { method: 'POST' }
    );
  }

  /**
   * Run a specific module on a commission statement
   *
   * Executes a predefined module on the commission statement to perform specific processing tasks.
   *
   * @param userId - The ID of the user who owns the commission statement
   * @param commissionStatementId - The ID of the commission statement
   * @param module - The name of the module to run (e.g., 'validate', 'calculate')
   * @returns Promise containing the results of the module execution
   */
  public runCommissionStatementModule(
    userId: number,
    commissionStatementId: number,
    module: CommissionStatementModuleName,
    mode: CommissionStatementModuleMode = 'plan',
    options: CommissionStatementResultAPIOptions = {}
  ): Promise<CommissionStatementResultPayload> {
    const finalOptions = { mode, ...options };

    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/module/${module}`,
      finalOptions,
      { method: 'POST' }
    );
  }

  /**
   * Transform a commission statement data file
   *
   * @param commissionStatementId - ID of the commission statement you want to process
   * @param userId - User whos commission statement you want to process
   * @returns Promise containing an instance of CommissionStatementPayload
   */
  public transformCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<CommissionStatementPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/transform`,
      null,
      { method: 'POST' }
    );
  }

  /**
   * Deletes a commission statement
   *
   * @param commissionStatementId - ID of the commission statement you want to delete
   * @param userId - User whos commission statement you want to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteCommissionStatement(
    userId: number,
    commissionStatementId: number
  ): Promise<DeleteResponse> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get commission statement results
   *
   * Retrieves the processing results for a specific commission statement,
   * including matched records, errors, and processing statistics.
   *
   * @param userId - The ID of the user who owns the commission statement
   * @param commissionStatementId - The ID of the commission statement
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the commission statement results
   */
  public getCommissionStatementResults(
    userId: number,
    commissionStatementId: number,
    options: object = {}
  ): Promise<CommissionStatementResultCollectionPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/results`,
      options
    );
  }

  /**
   * Get a specific commission statement result
   *
   * Retrieves details for a single commission statement processing result.
   *
   * @param userId - The ID of the user who owns the commission statement
   * @param commissionStatementId - The ID of the commission statement
   * @param commissionStatementResultId - The ID of the specific result
   * @param options - Additional query parameters
   * @returns Promise containing the commission statement result details
   */
  public getCommissionStatementResult(
    userId: number,
    commissionStatementId: number,
    commissionStatementResultId: number,
    options: object = {}
  ): Promise<CommissionStatementResultPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/results/${commissionStatementResultId}`,
      options
    );
  }

  /**
   * Create a commission statement result
   *
   * Manually creates a new result entry for a commission statement.
   * Typically used for corrections or manual additions.
   *
   * @param userId - The ID of the user who owns the commission statement
   * @param commissionStatementId - The ID of the commission statement
   * @param payload - The result data to create
   * @returns Promise containing the created commission statement result
   */
  public createCommissionStatementResult(
    userId: number,
    commissionStatementId: number,
    payload: CommissionStatementResultPayload
  ): Promise<CommissionStatementResultPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/${commissionStatementId}/results`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get a particular user's Commission Statement Mappings
   *
   * @param userId - User whos commission statements you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of CommissionStatementMappingCollectionPayload
   */
  public getCommissionStatementMappings(
    userId: number,
    options: object = {}
  ): Promise<CommissionStatementMappingCollectionPayload> {
    return this.sendRequest(`/users/${userId}/commission-statements/mappings`, options);
  }

  /**
   * Get a single Commission Statement Mapping for a particular user
   *
   * @param commissionStatementMappingId - Id of the commission statement you'd like to get
   * @param userId - User whos commission statements you want to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing an instance of CommissionStatementMappingPayload
   */
  public getCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number,
    options: object = {}
  ): Promise<CommissionStatementMappingPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/mappings/${commissionStatementMappingId}`,
      options
    );
  }

  /**
   * Update a commission statement mapping
   *
   * @param commissionStatementMappingId - ID of the commission statement you want to update
   * @param userId - User whos commission statement you want to update
   * @param payload - Object containig the updates values for the Commission Statement
   * @returns Promise containing an instance of CommissionStatementMappingPayload
   */
  public updateCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number,
    payload: CommissionStatementMappingPayload
  ): Promise<CommissionStatementMappingPayload> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/mappings/${commissionStatementMappingId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Create a commission statement mapping
   *
   * @param userId - User whos will own this commission statement
   * @param payload - Object containig the updates values for the Commission Statement
   * @returns Promise containing an instance of CommissionStatementMappingPayload
   */
  public createCommissionStatementMapping(
    userId: number,
    payload: CommissionStatementMappingPayload
  ): Promise<CommissionStatementMappingPayload> {
    return this.sendRequest(`/users/${userId}/commission-statements/mappings`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Deletes a commission statement mapping
   *
   * @param commissionStatementMappingId - ID of the commission statement you want to delete
   * @param userId - User whos commission statement you want to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteCommissionStatementMapping(
    userId: number,
    commissionStatementMappingId: number
  ): Promise<DeleteResponse> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/mappings/${commissionStatementMappingId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Update a commission statement mapping companies
   *
   * @param userId - User whos commission statement mapping you want to update
   * @param commissionStatementMappingId - ID of the commission statement mapping you want to update
   * @param payload - Object containig the updates values for the Commission Statement
   * @returns Promise containing an instance of CommissionStatementMappingPayload
   */
  public updateCommissionStatementMappingCompanies(
    userId: number,
    commissionStatementMappingId: number,
    payload: Array<CommissionStatementMappingCompanyPayload>
  ): Promise<Array<CommissionStatementMappingCompanyPayload>> {
    return this.sendRequest(
      `/users/${userId}/commission-statements/mappings/${commissionStatementMappingId}/companies`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get all available applications
   *
   * Retrieves a list of all applications available in the system.
   *
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the list of applications
   */
  public getApps(options: object = {}) {
    return this.sendRequest('/apps', options);
  }

  /**
   * Get a specific application by ID
   *
   * Retrieves detailed information about a specific application.
   *
   * @param appId - The ID of the application to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the application details
   */
  public getApp(appId: number, options: object = {}) {
    return this.sendRequest(`/apps/${appId}`, options);
  }

  /**
   * Get user integration provider details
   *
   * Retrieves information about a specific integration provider for a user.
   *
   * @param userIntegrationProviderId - The ID of the user integration provider
   * @param options - Additional query parameters
   * @returns Promise containing the integration provider details
   */
  public getUserIntegrationProvider(userIntegrationProviderId: number, options: object = {}) {
    return this.sendRequest(`/integrations/providers/${userIntegrationProviderId}`, options);
  }

  /**
   * Get a specific automation
   *
   * Retrieves detailed information about a single automation workflow.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the automation details
   */
  public getAutomation(userId: number, automationId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/automations/${automationId}`, options);
  }

  /**
   * Delete an automation
   *
   * Permanently removes an automation workflow and all its associated actions and schedules.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteAutomation(userId: number, automationId: number) {
    return this.sendRequest(`/users/${userId}/automations/${automationId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Update an automation
   *
   * Modifies the configuration and settings of an existing automation workflow.
   *
   * @param userId - The ID of the user who owns the automation
   * @param automationId - The ID of the automation to update
   * @param payload - The updated automation configuration
   * @returns Promise containing the updated automation details
   */
  public updateAutomation(userId: number, automationId: number, payload: AutomationPayload) {
    return this.sendRequest(`/users/${userId}/automations/${automationId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create a new automation
   *
   * Creates a new automation workflow for the specified user.
   *
   * @param userId - The ID of the user who will own the automation
   * @param payload - The automation configuration data
   * @returns Promise containing the created automation details
   */
  public createAutomation(userId: number, payload: AutomationPayload) {
    return this.sendRequest(`/users/${userId}/automations`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user integration OAuth calendars
   *
   * Retrieves a list of calendars from a user's integration with a third-party provider.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @returns Promise containing the list of calendars
   */
  public getUserIntegrationOAuthCalendars(userId: number, providerReference: string) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/${providerReference}/calendars`);
  }

  /**
   * Get user integration OAuth calendar events
   *
   * Retrieves a list of events from a specific calendar in a user's integration with a third-party provider.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param calendarId - The ID of the calendar to retrieve events from
   * @param params - Additional query parameters for filtering
   * @returns Promise containing the list of calendar events
   */
  public getUserIntegrationOAuthCalendarEvents(
    userId: number,
    providerReference: string,
    calendarId: string = null,
    params: any = {}
  ) {
    if (calendarId) {
      params.calendarId = calendarId;
    }
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/calendars/events`,
      params
    );
  }

  /**
   * Update a user integration OAuth calendar event
   *
   * Updates an existing calendar event in a user's integrated calendar service.
   * This allows modification of event details through the OAuth integration.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param eventId - The ID of the calendar event to update
   * @param payload - The updated event data
   * @returns Promise containing the updated calendar event
   */
  public updateUserIntegrationOAuthCalendarEvent(
    userId: number,
    providerReference: string,
    eventId: string,
    payload: any
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/calendars/events/${eventId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Delete a user integration OAuth calendar event
   *
   * Deletes a calendar event from a user's integrated calendar service.
   * This permanently removes the event from the external calendar.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param eventId - The ID of the calendar event to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserIntegrationOAuthCalendarEvent(
    userId: number,
    providerReference: string,
    eventId: string
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/calendars/events/${eventId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Create a user integration OAuth calendar event
   *
   * Creates a new calendar event in a user's integrated calendar service.
   * This allows the CRM to create events directly in external calendars.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param payload - The calendar event data to create
   * @returns Promise containing the created calendar event
   */
  public createUserIntegrationOAuthCalendarEvent(
    userId: number,
    providerReference: string,
    payload: any
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/calendars/events`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get user integration OAuth VoIP extensions
   *
   * Retrieves VoIP extensions from a user's OAuth-integrated VoIP provider.
   * Extensions are used for call routing and identification.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the VoIP provider
   * @param accountId - The account ID within the VoIP provider
   * @returns Promise containing the list of VoIP extensions
   */
  public getUserIntegrationOAuthVoipExtensions(
    userId: number,
    providerReference: string,
    accountId: number
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/${accountId}/extensions`
    );
  }

  /**
   * Get user integration OAuth VoIP phone numbers
   *
   * Retrieves phone numbers from a user's OAuth-integrated VoIP provider.
   * These numbers can be used for making and receiving calls.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the VoIP provider
   * @param accountId - The account ID within the VoIP provider
   * @returns Promise containing the list of VoIP phone numbers
   */
  public getUserIntegrationOAuthVoipPhoneNumbers(
    userId: number,
    providerReference: string,
    accountId: number
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/${accountId}/phone-numbers`
    );
  }

  /**
   * Get user integration OAuth VoIP phone numbers by extension
   *
   * Retrieves phone numbers associated with a specific extension from a
   * user's OAuth-integrated VoIP provider.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the VoIP provider
   * @param accountId - The account ID within the VoIP provider
   * @param extension - The extension number to get phone numbers for
   * @returns Promise containing the phone numbers for the specified extension
   */
  public getUserIntegrationOAuthVoipPhoneNumbersByExtension(
    userId: number,
    providerReference: string,
    accountId: number,
    extension: number
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/${accountId}/extensions/${extension}/phone-numbers`
    );
  }

  /**
   * Get user integration OAuth VoIP groups by extension
   *
   * Retrieves VoIP groups (ring groups, hunt groups, etc.) associated with
   * a specific extension from a user's OAuth-integrated VoIP provider.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the VoIP provider
   * @param accountId - The account ID within the VoIP provider
   * @param extension - The extension number to get groups for
   * @returns Promise containing the VoIP groups for the specified extension
   */
  public getUserIntegrationOAuthVoipGroupsByExtension(
    userId: number,
    providerReference: string,
    accountId: number,
    extension: number
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/${accountId}/extensions/${extension}/groups`
    );
  }

  /**
   * Get user integration OAuth VoIP phone number by ID
   *
   * Retrieves detailed information about a specific phone number from a
   * user's OAuth-integrated VoIP provider using the phone number ID.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the VoIP provider
   * @param accountId - The account ID within the VoIP provider
   * @param phoneId - The ID of the specific phone number
   * @returns Promise containing the phone number details
   */
  public getUserIntegrationOAuthVoipPhoneNumbersById(
    userId: number,
    providerReference: string,
    accountId: number,
    phoneId: number
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/${accountId}/phone-numbers/${phoneId}`
    );
  }

  /**
   * Deauthorize user integration OAuth
   *
   * Revokes OAuth authorization for a user's integration with a third-party
   * provider. This disconnects the integration and prevents further API access.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Additional options for the deauthorization process
   * @returns Promise containing the deauthorization confirmation
   */
  public deauthorizeUserIntegrationOAuth(
    userId: number,
    providerReference: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/${providerReference}/oauth/deauthorize`,
      options,
      { method: 'POST' }
    );
  }

  /**
   * Update user integration OAuth
   *
   * Updates OAuth integration settings for a user's connection to a third-party
   * provider. This can modify configuration, permissions, or other integration parameters.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @param options - Additional query parameters
   * @param payload - The updated integration configuration data
   * @returns Promise containing the updated integration information
   */
  public putUserIntegrationOAuth(
    userId: number,
    providerReference: string,
    options: object = {},
    payload: object = {}
  ) {
    return this.sendRequest(`/users/${userId}/integrations/${providerReference}/oauth`, options, {
      method: 'PUT',
      body: payload,
    });
  }

  /**
   * Get user personal token
   *
   * Retrieves a personal access token for a user for a specific application.
   * Personal tokens are used for authenticated API access.
   *
   * @param userId - The ID of the user
   * @param appReference - The reference identifier of the application
   * @param options - Additional query parameters
   * @returns Promise containing the personal token information
   */
  public getUserPersonalToken(userId: number, appReference: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/app/${appReference}/personal-token`, options);
  }

  /**
   * Get user token
   *
   * Retrieves a token for a user for a specific application.
   * Tokens are used for authenticated API access for oAuth.
   *
   * @param userId - The ID of the user
   * @param appId - The identifier of the application
   * @param options - Additional query parameters
   * @returns Promise containing the personal token information
   */
  public getUserAppToken(userId: number, appId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/apps/${appId}/token`, options);
  }

  /**
   * Get user integration webhooks
   *
   * Retrieves available webhook configurations for a user's integration
   * with a specific provider. Webhooks enable real-time notifications.
   *
   * @param userId - The ID of the user
   * @param providerReference - The reference identifier of the integration provider
   * @returns Promise containing the webhook configuration details
   */
  public getUserIntegrationWebhooks(userId: number, providerReference: string) {
    return this.sendRequest(`/integrations/oauth/${providerReference}/webhooks`);
  }

  /**
   * Get user integrations by feature
   *
   * Retrieves OAuth integrations available to a user for a specific feature.
   * This helps determine which third-party services the user can connect to
   * for a particular functionality.
   *
   * @param userId - The ID of the user
   * @param feature - The feature name to check integration availability for
   * @returns Promise containing available integrations for the specified feature
   */
  public getUserIntegrationByFeature(userId: number, feature: string) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/provider-feature-availability/${feature}`
    );
  }

  /**
   * Get user OAuth integrations
   *
   * Retrieves all OAuth integrations currently configured for a user.
   * This includes connected third-party services and their authorization status.
   *
   * @param userId - The ID of the user
   * @returns Promise containing the user's OAuth integration configurations
   */
  public getUserIntegrationsOAuth(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth`);
  }

  /**
   * Get user integration feature availability
   *
   * Checks which integration providers are available to a specific user
   * for a particular feature. This considers user permissions, agency settings,
   * and feature-specific provider compatibility.
   *
   * @param userId - The ID of the user to check availability for
   * @param feature - The specific feature to check provider availability
   * @returns Promise containing array of available integration providers
   */
  public getUserIntegrationFeatureAvailability(
    userId: number,
    feature: string
  ): Promise<Array<UserIntegrationFeatureAvailabilityResponse>> {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/provider-feature-availability-by-user/${feature}`
    );
  }

  /**
   * Get user VoIP extensions
   *
   * Retrieves available VoIP extensions for a user from a specific provider.
   * Extensions are used for routing calls to the correct user or department.
   *
   * @param userId - The ID of the user
   * @param provider - The VoIP provider ID
   * @param options - Additional query parameters
   * @returns Promise containing the available VoIP extensions
   */
  public getUserVoipExtensions(userId: number, provider: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-extensions/${provider}`, options);
  }

  /**
   * Get user VoIP phone numbers by provider
   *
   * Retrieves phone numbers associated with a user from a specific VoIP provider.
   * These numbers can be used for making and receiving calls through the system.
   *
   * @param userId - The ID of the user
   * @param provider - The VoIP provider ID
   * @param options - Additional query parameters
   * @returns Promise containing the user's VoIP phone numbers
   */
  public getUserVoipPhoneNumbers(userId: number, provider: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-phone-numbers/${provider}`, options);
  }

  /**
   * Get user VoIP phone numbers by application
   *
   * Retrieves phone numbers associated with a user from a specific application.
   * This is useful when the same user has phone numbers across different apps
   * or integrations.
   *
   * @param userId - The ID of the user
   * @param app - The application ID
   * @param options - Additional query parameters
   * @returns Promise containing the user's phone numbers for the specified app
   */
  public getUserVoipPhoneNumbersApp(userId: number, app: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-phone-numbers-app/${app}`, options);
  }

  /**
   * Create a personal access token for a user
   *
   * Generates a new personal access token for a user to access a specific
   * application or service. These tokens provide secure API access without
   * requiring password authentication.
   *
   * @param userId - The ID of the user
   * @param appReference - The reference identifier for the target application
   * @param payload - Token configuration data
   * @returns Promise containing the created personal access token
   */
  public createUserPersonalToken(userId: number, appReference: string, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/app/${appReference}/personal-token`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Delete a user's personal access token
   *
   * Revokes and deletes a personal access token for a specific application.
   * This immediately invalidates the token and prevents further API access.
   *
   * @param userId - The ID of the user
   * @param appReference - The reference identifier for the target application
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserPersonalToken(userId: number, appReference: string) {
    return this.sendRequest(`/users/${userId}/app/${appReference}/personal-token`, null, {
      method: 'DELETE',
    });
  }
  public updateUserPersonalToken(
    userId: number,
    appReference: string,
    tokenId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/app/${appReference}/personal-token/${tokenId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Get agency personal token
   *
   * Retrieves a personal access token for an agency for a specific application.
   * Personal tokens are used for authenticated API access.
   *
   * @param agencyId - The ID of the agency
   * @param appReference - The reference identifier of the application
   * @param options - Additional query parameters
   * @returns Promise containing the personal token information
   */
  public getAgencyPersonalToken(agencyId: number, appReference: string, options: object = {}) {
    return this.sendRequest(`/agencies/${agencyId}/app/${appReference}/personal-token`, options);
  }

  /**
   * Create a personal access token for an agency
   *
   * Generates a new personal access token for an agency to access a specific
   * application or service. These tokens provide secure API access without
   * requiring password authentication.
   *
   * @param agencyId - The ID of the agency
   * @param appReference - The reference identifier for the target application
   * @param payload - Token configuration data
   * @returns Promise containing the created personal access token
   */
  public createAgencyPersonalToken(agencyId: number, appReference: string, payload: object = {}) {
    return this.sendRequest(`/agencies/${agencyId}/app/${appReference}/personal-token`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Delete an agencies personal access token
   *
   * Revokes and deletes a personal access token for a specific application.
   * This immediately invalidates the token and prevents further API access.
   *
   * @param agencyId - The ID of the agency
   * @param appReference - The reference identifier for the target application
   * @returns Promise containing the deletion confirmation
   */
  public deleteAgencyPersonalToken(agencyId: number, appReference: string) {
    return this.sendRequest(`/agencies/${agencyId}/app/${appReference}/personal-token`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get all available individual sources
   *
   * Retrieves a list of all available sources that can be associated with
   * individuals in the system. Sources track where individuals came from
   * (e.g., referrals, marketing campaigns, etc.).
   *
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the list of available individual sources
   */
  public getUserSources(userId: number, options: UserSourcesAPIOptions) {
    return this.sendRequest(`/users/${userId}/sources`, options);
  }

  /**
   * Add user source
   *
   * Creates a new source for a user.
   *
   * @param userId - The ID of the user to add the source for
   * @param payload - source configuration data
   * @returns Promise containing the created notification preference
   */
  public addUserSource(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/sources`, null, { method: 'POST', body: payload });
  }

  /**
   * Create a new address for an individual
   *
   * Adds a new address record to an individual's profile. This can be used
   * for home, work, mailing, or other address types.
   *
   * @param indId - The ID of the individual
   * @param payload - The address data to create
   * @returns Promise containing the created address information
   */
  public createIndAddress(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/addresses`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an existing address for an individual
   *
   * Modifies an existing address record with new information. Useful for
   * correcting address details or updating when someone moves.
   *
   * @param indId - The ID of the individual who owns the address
   * @param indAddressId - The ID of the address to update
   * @param payload - The updated address data
   * @returns Promise containing the updated address information
   */
  public updateIndAddress(indId: number, indAddressId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/addresses/${indAddressId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove an address from an individual
   *
   * Permanently deletes an address record from an individual's profile.
   * This action cannot be undone.
   *
   * @param indId - The ID of the individual who owns the address
   * @param indAddressId - The ID of the address to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndAddress(indId: number, indAddressId: number) {
    return this.sendRequest(`/individuals/${indId}/addresses/${indAddressId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Move individual address
   *
   * Transfers an address record from one individual to another.
   *
   * @param individualId - The ID of the individual who owns the address
   * @param individualAddressId - The ID of the address to move
   * @param payload - Transfer details including destination individual
   * @returns Promise containing the move operation results
   */
  public moveIndAddress(individualId: number, individualAddressId: number, payload: any[]) {
    return this.sendRequest(
      `/individuals/${individualId}/addresses/${individualAddressId}/move`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Move individual documents
   *
   * Transfers document records from one individual to another.
   *
   * @param userId - The ID of the user performing the move
   * @param individualId - The ID of the individual whose documents to move
   * @param payload - Transfer details including destination individual
   * @returns Promise containing the move operation results
   */
  public moveIndDocuments(userId: number, individualId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/documents/${individualId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create individual email
   *
   * Adds a new email address to an individual's contact information.
   *
   * @param indId - The ID of the individual to add email for
   * @param payload - Email data including address and type
   * @returns Promise containing the created email details
   */
  public createIndEmail(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/emails`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update individual email
   *
   * Modifies an existing email address for an individual.
   *
   * @param indId - The ID of the individual who owns the email
   * @param indPhoneId - The ID of the email record to update
   * @param payload - Updated email data
   * @returns Promise containing the updated email details
   */
  public updateIndEmail(indId: number, indPhoneId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/emails/${indPhoneId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove individual email
   *
   * Permanently deletes an email address from an individual's record.
   *
   * @param indId - The ID of the individual who owns the email
   * @param indPhoneId - The ID of the email record to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndEmail(indId: number, indPhoneId: number) {
    return this.sendRequest(`/individuals/${indId}/emails/${indPhoneId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Move individual email
   *
   * Transfers an email record from one individual to another.
   *
   * @param individualId - The ID of the individual who owns the email
   * @param individualEmailId - The ID of the email record to move
   * @param payload - Transfer details including destination individual
   * @returns Promise containing the move operation results
   */
  public moveIndEmail(individualId: number, individualEmailId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/emails/${individualEmailId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create individual Medicare record
   *
   * Adds Medicare information to an individual's healthcare records.
   *
   * @param indId - The ID of the individual to add Medicare information for
   * @param payload - Medicare data including numbers and coverage details
   * @returns Promise containing the created Medicare record details
   */
  public createIndMedicare(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/medicare`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update individual Medicare record
   *
   * Modifies existing Medicare information for an individual.
   *
   * @param indId - The ID of the individual who owns the Medicare record
   * @param indMedicareId - The ID of the Medicare record to update
   * @param payload - Updated Medicare data
   * @returns Promise containing the updated Medicare record details
   */
  public updateIndMedicare(indId: number, indMedicareId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/medicare/${indMedicareId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual Medicare record
   *
   * Transfers a Medicare record from one individual to another.
   *
   * @param individualId - The ID of the individual who owns the Medicare record
   * @param individualMedicareId - The ID of the Medicare record to move
   * @param payload - Transfer details including destination individual
   * @returns Promise containing the move operation results
   */
  public moveIndMedicare(individualId: number, individualMedicareId: number, payload: any[]) {
    return this.sendRequest(
      `/individuals/${individualId}/medicare/${individualMedicareId}/move`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Create individual document
   *
   * Adds a document to an individual's record.
   *
   * @param indId - The ID of the individual to add document for
   * @param payload - Document data including file information and metadata
   * @returns Promise containing the created document details
   */
  public createIndDocument(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/document`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Create individual social security record
   *
   * Adds social security information to an individual's record.
   *
   * @param indId - The ID of the individual to add social security info for
   * @param payload - Social security data including SSN and related information
   * @returns Promise containing the created social security record details
   */
  public createIndSocial(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/social`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update individual social security record
   *
   * Modifies existing social security information for an individual.
   *
   * @param indId - The ID of the individual who owns the social security record
   * @param indSocialId - The ID of the social security record to update
   * @param payload - Updated social security data
   * @returns Promise containing the updated social security record details
   */
  public updateIndSocial(indId: number, indSocialId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/social/${indSocialId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create individual Medicaid record
   *
   * Adds Medicaid information to an individual's record, including
   * coverage details and eligibility information.
   *
   * @param indId - The ID of the individual to add Medicaid info for
   * @param payload - Medicaid data including coverage and eligibility details
   * @returns Promise containing the created Medicaid record details
   */
  public createIndMedicaid(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/medicaid`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update individual Medicaid record
   *
   * Modifies existing Medicaid information for an individual.
   *
   * @param indId - The ID of the individual who owns the Medicaid record
   * @param indMedicaidId - The ID of the Medicaid record to update
   * @param payload - Updated Medicaid data
   * @returns Promise containing the updated Medicaid record details
   */
  public updateIndMedicaid(indId: number, indMedicaidId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/medicaid/${indMedicaidId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual Medicaid record
   *
   * Transfers Medicaid record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the Medicaid record
   * @param individualMedicaidId - The ID of the Medicaid record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndMedicaid(individualId: number, individualMedicaidId: number, payload: any[]) {
    return this.sendRequest(
      `/individuals/${individualId}/medicaid/${individualMedicaidId}/move`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Move individual notes
   *
   * Transfers notes from one individual to another. Used for
   * record corrections and data consolidation scenarios.
   *
   * @param userId - The ID of the user performing the move operation
   * @param individualId - The current owner of the notes
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndNotes(userId: number, individualId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/notes/${individualId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual phone record
   *
   * Transfers phone record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the phone record
   * @param individualPhoneId - The ID of the phone record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndPhone(individualId: number, individualPhoneId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/phones/${individualPhoneId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual policy record
   *
   * Transfers policy record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the policy record
   * @param policyId - The ID of the policy record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndPolicy(individualId: number, policyId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/policy/${policyId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual prescription drug record
   *
   * Transfers prescription drug record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the drug record
   * @param indDrugId - The ID of the drug record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndDrug2(individualId: number, indDrugId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/rx/${indDrugId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual provider record
   *
   * Transfers provider record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the provider record
   * @param indProviderId - The ID of the provider record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndProvider(individualId: number, indProviderId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/providers/${indProviderId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move individual pharmacy record
   *
   * Transfers pharmacy record ownership from one individual to another.
   * Used for record corrections and data migration scenarios.
   *
   * @param individualId - The current owner of the pharmacy record
   * @param indPharmacyId - The ID of the pharmacy record to move
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveIndPharmacy(individualId: number, indPharmacyId: number, payload: any[]) {
    return this.sendRequest(`/individuals/${individualId}/pharmacies/${indPharmacyId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move user individual call logs
   *
   * Transfers call log records from one individual to another within a user's account.
   * Used for record corrections and data consolidation scenarios.
   *
   * @param userId - The ID of the user who owns the call logs
   * @param individualId - The current owner of the call logs
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveUserIndCallLogs(userId: number, individualId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/call-logs/${individualId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move user individual SMS threads
   *
   * Transfers SMS conversation threads from one individual to another within a user's account.
   * Used for record corrections and data consolidation scenarios.
   *
   * @param userId - The ID of the user who owns the SMS threads
   * @param individualId - The current owner of the SMS threads
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveUserIndSmsThreads(userId: number, individualId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/sms-threads/${individualId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Move user Statement of Applicability (SOA) records
   *
   * Transfers SOA records from one individual to another within a user's account.
   * Used for record corrections and data consolidation scenarios.
   *
   * @param userId - The ID of the user who owns the SOA records
   * @param individualId - The current owner of the SOA records
   * @param payload - Move operation data including target individual ID
   * @returns Promise containing the move operation results
   */
  public moveUserSoa(userId: number, individualId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/soas/${individualId}/move`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getIndNotes(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/notes`, options);
  }
  public getIndPhones(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/phones`, options);
  }
  public getIndEmails(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/emails`, options);
  }
  public nativeEmailSend(payload: object = {}) {
    return this.sendRequest('/native/email/send', null, { method: 'POST', body: payload });
  }

  /**
   * Get user policies
   *
   * Retrieves policies owned by a current user.
   *
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the user's policies
   */
  public getPolicies(options: object = {}) {
    return this.sendRequest('/policies', options);
  }

  /**
   * Get total user policies
   *
   * Retrieves total policies owned by a current user.
   *
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the total user's policies
   */
  public getPoliciesTotal(options: object = {}) {
    return this.sendRequest('/policies/total', options);
  }

  /**
   * Get user policies by type
   *
   * Retrieves policies owned by a specific user filtered by policy type.
   *
   * @param userId - The ID of the user who owns the policies
   * @param type - The type of policies to retrieve
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the user's policies of the specified type
   */
  public getUserPolicies(userId: number, type: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/policies/${type}`, options);
  }

  /**
   * Get user AEP (Annual Enrollment Period) production data
   *
   * Retrieves production statistics and metrics for a user during the Annual Enrollment Period.
   *
   * @param userId - The ID of the user to retrieve AEP production data for
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the user's AEP production statistics
   */
  public getUserAEPProduction(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/policies/aep-production`, options);
  }

  /**
   * Get user production data by type
   *
   * Retrieves production statistics and metrics for a user filtered by type.
   *
   * @param userId - The ID of the user to retrieve production data for
   * @param type - The type of production data to retrieve
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the user's production statistics
   */
  public getUserProduction(userId: number, type: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/policies/production/${type}`, options);
  }

  /**
   * Get user production data by year and type
   *
   * Retrieves production statistics and metrics for a user filtered by year and type.
   *
   * @param userId - The ID of the user to retrieve production data for
   * @param year - The specific year to retrieve data for
   * @param type - The type of production data to retrieve
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the user's yearly production statistics
   */
  public getUserProductionByYear(userId: number, year: number, type: string, options: object = {}) {
    return this.sendRequest(
      `/users/${userId}/policies/production-by-year/${year}/${type}`,
      options
    );
  }

  /**
   * Get user policies grouped by field
   *
   * Retrieves policies organized into groups based on a specified field value.
   * Useful for creating summary reports and analytics.
   *
   * @param fieldName - The field to group policies by
   * @param ownerId - The ID of the user who owns the policies
   * @returns Promise containing the grouped policy data
   */
  public getUserPoliciesGrouped(fieldName: string, ownerId: number) {
    const options = {};
    if (fieldName) options['field'] = fieldName;
    if (ownerId) options['ownerId'] = ownerId;
    return this.sendRequest(`/users/${ownerId}/policies/grouped`, options);
  }

  /**
   * Get user staff members
   *
   * Retrieves a list of staff members associated with a user's account.
   *
   * @param userId - The ID of the user who owns the staff
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the list of staff members
   */
  public getUserStaff(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/staff`, options);
  }

  /**
   * Get agency staff members
   *
   * Retrieves a list of staff members associated with an agency.
   *
   * @param userId - The ID of the user who owns the agency
   * @param agencyId - The ID of the agency who owns the staff
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the list of staff members
   */
  public getAgencyStaff(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/agency/${agencyId}/staff`, options);
  }

  /**
   * Update user staff member
   *
   * Updates information for a specific staff member in a user's account.
   *
   * @param userId - The ID of the user who owns the staff member
   * @param staffId - The ID of the staff member to update
   * @param payload - The updated staff member data
   * @returns Promise containing the updated staff member information
   */
  public updateUserStaff(userId: number, staffId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/staff/${staffId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Get user insurance plans by type
   *
   * Retrieves insurance plans available to a user filtered by plan type.
   *
   * @param userId - The ID of the user
   * @param type - The type of plans to retrieve
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the user's available plans
   */
  public getUserPlans(userId: number, type: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/plans/${type}`, options);
  }

  /**
   * Get all insurance plans
   *
   * Retrieves a complete list of insurance plans available in the system.
   *
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the list of all plans
   */
  public getPlans(options: object = {}) {
    return this.sendRequest('/plan/', options);
  }

  /**
   * Get user plan types
   *
   * Retrieves the different types of insurance plans available to a user.
   *
   * @param userId - The ID of the user
   * @param options - Additional query parameters
   * @returns Promise containing the list of plan types
   */
  public getUserPlanTypes(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/plans/types`, options);
  }

  /**
   * Get user commission information
   *
   * Retrieves commission information for a user based on company and plan criteria.
   *
   * @param userId - The ID of the user
   * @param companyId - The ID of the insurance company
   * @param planId - The ID of the insurance plan
   * @param options - Additional query parameters
   * @returns Promise containing the commission information
   */
  public getUserCommission(userId: number, companyId: number, planId: number, options: {} = null) {
    if (!options) {
      options = {};
    }
    options['criteria'] = {
      company: companyId,
      plan: planId,
    };
    return this.sendRequest(`/users/${userId}/commissions`, options).then((response) => {
      if (response.data.length > 0) {
        return response.data[0];
      }
      return null;
    });
  }

  /**
   * Get user commissions by type
   *
   * Retrieves commission records for a user filtered by commission type.
   *
   * @param userId - The ID of the user
   * @param type - The type of commissions to retrieve
   * @param options - Additional query parameters for filtering and pagination
   * @returns Promise containing the commission records
   */
  public getUserCommissions(userId: number, type: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/${type}`, options);
  }

  /**
   * Get user commissions schedule
   *
   * Retrieves the commission payment schedule for a user.
   *
   * @param userId - The ID of the user
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the commission schedule data
   */
  public getUserCommissionsSchedule(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/schedule`, options);
  }

  /**
   * Export user commissions schedule
   *
   * Generates an export of the commission payment schedule for a user.
   *
   * @param userId - The ID of the user
   * @param options - Export options and parameters
   * @returns Promise containing the export data
   */
  public getUserCommissionsScheduleExport(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/schedule/export`, options);
  }
  public updateUserCommission(userId: number, commissionId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/${commissionId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getIndMarxs(options: object = {}) {
    return this.sendRequest('/individuals/marx', options);
  }
  public getGlobProviderTypes(criteria: object = {}) {
    const options = {};
    if (criteria) options['criteria'] = criteria;
    return this.sendRequest('/provider-types', options);
  }
  public getGlobCountyZips(criteria: object = {}) {
    const options = {};
    if (criteria) options['criteria'] = criteria;
    return this.sendRequest('/counties', options);
  }
  public getGlobCompanies(criteria: object = {}) {
    return this.sendRequest('/companies', criteria);
  }
  public getGlobPlans(criteria: object = {}) {
    const options = {};
    if (criteria) options['criteria'] = criteria;
    return this.sendRequest('/plans', options);
  }
  public getIndMarxsTotal(options: object = {}, criteria: {}) {
    if (!options) {
      options = {};
    }
    if (criteria) {
      options['criteria'] = criteria;
    }
    return this.getIndMarxs(options).then((response) => {
      let futureCount = 0;
      let currentCount = 0;
      let deceasedCount = 0;
      let badMbiCount = 0;
      response.data.forEach((item) => {
        if (item.currentPlanFlag) {
          currentCount++;
        }
        if (item.futurePlanFlag) {
          futureCount++;
        }
        if (item.deceasedFlag) {
          deceasedCount++;
        }
        if (item.badMbiFlag) {
          badMbiCount++;
        }
      });
      const data = [
        { flag: 'currentPlanFlag', total: currentCount, label: 'Current Plan' },
        { flag: 'futurePlanFlag', total: futureCount, label: 'Future Plan' },
        { flag: 'deceasedFlag', total: deceasedCount, label: 'Deceased' },
        { flag: 'badMbiFlag', total: badMbiCount, label: 'Bad MBI' },
      ];
      return data;
    });
  }

  /**
   * Update individual Marx data
   *
   * Updates Medicare Advantage Prescription (Marx) data for an individual.
   * This includes enrollment status and plan information.
   *
   * @param id - The ID of the individual Marx record to update
   * @param payload - The updated Marx data
   * @returns Promise containing the updated Marx record
   */
  public updateIndMarx(id: number, payload: object = {}) {
    return this.sendRequest(`/individuals/marx/${id}`, null, { method: 'PATCH', body: payload });
  }

  /**
   * Get individual Marx data grouped
   *
   * Retrieves individual Marx data organized into groups for reporting
   * and analytics purposes.
   *
   * @param options - Query parameters for grouping and filtering
   * @returns Promise containing the grouped Marx data
   */
  public getIndMarxGrouped(options: object = {}) {
    return this.sendRequest('/individuals/marx/grouped', options);
  }

  /**
   * Create a user commission
   *
   * Creates a new commission record for a user.
   *
   * @param userId - The ID of the user who will own the commission
   * @param payload - The commission data to create
   * @returns Promise containing the created commission details
   */
  public createUserCommission(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user alerts
   *
   * Retrieves all alerts and notifications for a specific user.
   *
   * @param userId - The ID of the user whose alerts to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's alerts
   */
  public getUserAlerts(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/alerts`, options);
  }

  /**
   * Add a user alert
   *
   *
   * @param userId - The ID of the user who owns the alert
   * @param payload - The alert data
   * @returns Promise containing the alert details
   */
  public addUserAlert(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/alerts`, null, { method: 'POST', body: payload });
  }

  /**
   * Update a user alert
   *
   * Modifies the status or configuration of a user alert.
   *
   * @param userId - The ID of the user who owns the alert
   * @param alertId - The ID of the alert to update
   * @param payload - The updated alert data
   * @returns Promise containing the updated alert details
   */
  public updateUserAlert(userId: number, alertId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/alerts/${alertId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Update user settings
   *
   * Updates configuration settings and preferences for a user account.
   *
   * @param userId - The ID of the user whose settings to update
   * @param payload - The updated settings data
   * @returns Promise containing the updated settings
   */
  public updateUserSetting(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/settings`, null, { method: 'PATCH', body: payload });
  }

  /**
   * Place a VoIP call through OAuth integration
   *
   * Initiates a phone call using an OAuth-integrated VoIP provider.
   *
   * @param userId - The ID of the user placing the call
   * @param provider - The VoIP provider reference
   * @param accountId - The account ID with the VoIP provider
   * @param payload - Call data including destination number
   * @returns Promise containing the call initiation response
   */
  public placeUserIntegrationOAuthVoipCall(
    userId: number,
    provider: string,
    accountId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${provider}/${accountId}/call`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Add contacts to user integration via OAuth
   *
   * Adds contacts to a user's integrated service (e.g., email marketing, CRM).
   *
   * @param userId - The ID of the user performing the operation
   * @param provider - The integration provider reference
   * @param payload - The contact data to add
   * @returns Promise containing the operation result
   */
  public addUserIntegrationOAuthContacts(userId: number, provider: string, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/${provider}/add-contacts`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Add contacts to user integration via PAT
   *
   * Adds contacts to a user's integrated service (e.g., email marketing, CRM).
   *
   * @param provider - The integration provider reference
   * @param payload - The contact data to add
   * @returns Promise containing the operation result
   */
  public addIntegrationPatContacts(provider: string, payload: object = {}) {
    return this.sendRequest(`/integrations/pat/${provider}/add-contacts`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Add individual alternate IDs
   *
   * Creates alternate identifiers for individuals to help with data matching
   * and integration with external systems.
   *
   * @param userId - The ID of the user performing the operation
   * @param payload - The alternate ID data to create
   * @returns Promise containing the operation result
   */
  public addIndAlternateIds(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/alternate-ids`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get individual alternate IDs
   *
   * Retrieves all alternate identifiers associated with a specific individual.
   *
   * @param userId - The ID of the user who owns the individual
   * @param individualId - The ID of the individual
   * @param options - Additional query parameters
   * @returns Promise containing the list of alternate IDs
   */
  public getIndAlternateIds(userId: number, individualId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/${individualId}/alternate-ids`, options);
  }

  /**
   * Remove an individual alternate ID
   *
   * Permanently removes a specific alternate identifier from an individual's record.
   *
   * @param userId - The ID of the user who owns the individual
   * @param individualId - The ID of the individual
   * @param alternateId - The ID of the alternate identifier to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndAlternateId(userId: number, individualId: number, alternateId: number) {
    return this.sendRequest(
      `/users/${userId}/individuals/${individualId}/alternate-ids/${alternateId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get individual pharmacies
   *
   * Retrieves pharmacy information associated with an individual.
   *
   * @param indId - The ID of the individual
   * @param options - Query parameters for filtering
   * @returns Promise containing the individual's pharmacy information
   */
  public getIndPharmacies(indId: number, options: object = {}) {
    return this.sendRequest(`/individual/${indId}/pharmacies`, options);
  }

  /**
   * Add individual connection
   *
   * Creates a relationship connection between individuals (e.g., family members, spouses).
   *
   * @param userId - The ID of the user performing the operation
   * @param individualId - The ID of the primary individual
   * @param payload - The connection data including the connected individual
   * @returns Promise containing the created connection details
   */
  public addIndConnection(userId: number, individualId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/${individualId}/connections`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get individual connections
   *
   * Retrieves all relationship connections for a specific individual.
   *
   * @param userId - The ID of the user who owns the individual
   * @param individualId - The ID of the individual
   * @param options - Query parameters for filtering
   * @returns Promise containing the list of connections
   */
  public getIndConnections(userId: number, individualId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/${individualId}/connections`, options);
  }

  /**
   * Remove individual connection
   *
   * Removes a relationship connection between individuals.
   *
   * @param userId - The ID of the user performing the operation
   * @param individualId - The ID of the primary individual
   * @param payload - The connection removal data
   * @returns Promise containing the operation result
   */
  public removeIndConnection(userId: number, individualId: number, payload: object = {}) {
    return this.sendRequest(
      `/users/${userId}/individuals/${individualId}/connections-delete`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Update individual connection
   *
   * Modifies an existing relationship connection between individuals.
   *
   * @param userId - The ID of the user performing the operation
   * @param individualId - The ID of the primary individual
   * @param connectionId - The ID of the connection to update
   * @param payload - The updated connection data
   * @returns Promise containing the updated connection details
   */
  public updateIndConnection(
    userId: number,
    individualId: number,
    connectionId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/individuals/${individualId}/connections/${connectionId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }
  public addIndAppAlternateIds(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/app-alternate-ids`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public getIndAppAlternateIds(userId: number, individualId: number, options: object = {}) {
    return this.sendRequest(
      `/users/${userId}/individuals/${individualId}/app-alternate-ids`,
      options
    );
  }
  public removeIndAppAlternateId(userId: number, individualId: number, alternateId: number) {
    return this.sendRequest(
      `/users/${userId}/individuals/${individualId}/app-alternate-ids/${alternateId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get a policy by ID
   *
   * Retrieves detailed information about a specific policy.
   *
   * @param policyId - The ID of the policy to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the policy details
   */
  public getPolicy(policyId: number, options: object = {}) {
    return this.sendRequest(`/policies/${policyId}`, options);
  }

  /**
   * Get an individual's policy
   *
   * Retrieves a specific policy associated with an individual.
   *
   * @param indId - The ID of the individual
   * @param policyId - The ID of the policy
   * @param options - Additional query parameters
   * @returns Promise containing the policy details
   */
  public getIndPolicy(indId: number, policyId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/policies/${policyId}`, options);
  }

  /**
   * Add a policy to an individual
   *
   * Creates a new policy record associated with an individual.
   *
   * @param indId - The ID of the individual
   * @param payload - The policy data to create
   * @returns Promise containing the created policy details
   */
  public addIndPolicy(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/policies`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an individual's policy
   *
   * Modifies an existing policy associated with an individual.
   *
   * @param indId - The ID of the individual
   * @param policyId - The ID of the policy to update
   * @param payload - The updated policy data
   * @returns Promise containing the updated policy details
   */
  public updateIndPolicy(indId: number, policyId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/policies/${policyId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create a policy group
   *
   * Creates a group association for a policy, useful for family plans or group coverage.
   *
   * @param policyId - The ID of the policy
   * @param payload - The group configuration data
   * @returns Promise containing the created group details
   */
  public createIndPolicyGroup(policyId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/policies/${policyId}/groups`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Remove a policy group
   *
   * Removes a group association from a policy.
   *
   * @param groupId - The ID of the group to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndPolicyGroup(groupId: number) {
    return this.sendRequest(`/individuals/policies/groups/${groupId}`, null, { method: 'DELETE' });
  }

  /**
   * Get an individual's SOA (Statement of Account)
   *
   * Retrieves a specific Statement of Account for an individual using its UUID.
   *
   * @param indId - The ID of the individual
   * @param uuid - The unique identifier of the SOA
   * @param options - Additional query parameters
   * @returns Promise containing the SOA details
   */
  public getIndSoa(indId: number, uuid: string, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soas/${uuid}`, options);
  }

  /**
   * Get all SOAs for an individual
   *
   * Retrieves all Statement of Account records associated with an individual.
   *
   * @param indId - The ID of the individual
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the list of SOAs
   */
  public getIndSoas(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soas`, options);
  }

  /**
   * Create SOA status for an individual
   *
   * Creates a new status record for an individual's Statement of Account.
   *
   * @param indId - The ID of the individual
   * @param payload - The SOA status data
   * @returns Promise containing the created status details
   */
  public createIndSoaStatus(indId: number, payload: object) {
    return this.sendRequest(`/individuals/${indId}/soa-status`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get SOA status for an individual
   *
   * Retrieves the current status of an individual's Statement of Account.
   *
   * @param indId - The ID of the individual
   * @param options - Additional query parameters
   * @returns Promise containing the SOA status information
   */
  public getIndSoaStatus(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soa-status`, options);
  }

  /**
   * Create a new SOA for an individual
   *
   * Creates a new Statement of Account record for an individual.
   *
   * @param indId - The ID of the individual
   * @param payload - The SOA data to create
   * @returns Promise containing the created SOA details
   */
  public createIndSoa(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soas`, null, { method: 'POST', body: payload });
  }

  /**
   * Update an individual's SOA
   *
   * Modifies an existing Statement of Account for an individual.
   *
   * @param indId - The ID of the individual
   * @param soaId - The ID of the SOA to update
   * @param payload - The updated SOA data
   * @returns Promise containing the updated SOA details
   */
  public updateIndSoa(indId: number, soaId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soas/${soaId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Update SOA code for an individual
   *
   * Updates the status code of an individual's Statement of Account.
   *
   * @param indId - The ID of the individual
   * @param soaId - The ID of the SOA
   * @param payload - The updated code data
   * @returns Promise containing the updated SOA code information
   */
  public updateIndSoaCode(indId: number, soaId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/soas/${soaId}/code`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove an individual's SOA
   *
   * Permanently deletes a Statement of Account record for an individual.
   *
   * @param indId - The ID of the individual
   * @param soaId - The ID of the SOA to delete
   * @returns Promise containing the deletion confirmation
   */
  public removeIndSoa(indId: number, soaId: number) {
    return this.sendRequest(`/individuals/${indId}/soas/${soaId}`, null, { method: 'DELETE' });
  }
  public createIndPlanYearReview(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/plan-year-reviews`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public removeIndPlanYearReview(indId: number, reviewId: number) {
    return this.sendRequest(`/individuals/${indId}/plan-year-reviews/${reviewId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create a phone number for an individual
   *
   * Adds a new phone number entry to an individual's contact information.
   *
   * @param indId - The ID of the individual
   * @param payload - The phone number data to create
   * @returns Promise containing the created phone entry
   */
  public createIndPhone(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/phones`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an individual's phone number
   *
   * Modifies an existing phone number entry for an individual.
   *
   * @param indId - The ID of the individual
   * @param indPhoneId - The ID of the phone entry to update
   * @param payload - The updated phone data
   * @returns Promise containing the updated phone entry
   */
  public updateIndPhone(indId: number, indPhoneId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/phones/${indPhoneId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove a phone number from an individual
   *
   * Deletes a phone number entry from an individual's contact information.
   *
   * @param indId - The ID of the individual
   * @param indPhoneId - The ID of the phone entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndPhone(indId: number, indPhoneId: number) {
    return this.sendRequest(`/individuals/${indId}/phones/${indPhoneId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create a dependent for an individual
   *
   * Adds a new dependent (family member) to an individual's record.
   *
   * @param indId - The ID of the individual
   * @param payload - The dependent data to create
   * @returns Promise containing the created dependent entry
   */
  public createIndDependent(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/dependents`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an individual's dependent information
   *
   * Modifies an existing dependent entry for an individual.
   *
   * @param indId - The ID of the individual
   * @param dependentId - The ID of the dependent to update
   * @param payload - The updated dependent data
   * @returns Promise containing the updated dependent entry
   */
  public updateIndDependent(indId: number, dependentId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/dependents/${dependentId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove a dependent from an individual
   *
   * Deletes a dependent entry from an individual's record.
   *
   * @param indId - The ID of the individual
   * @param dependentId - The ID of the dependent to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndDependent(indId: number, dependentId: number) {
    return this.sendRequest(`/individuals/${indId}/dependents/${dependentId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create health information for an individual
   *
   * Adds a new health record entry to an individual's medical information.
   *
   * @param indId - The ID of the individual
   * @param payload - The health data to create
   * @returns Promise containing the created health entry
   */
  public createIndHealth(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/health`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an individual's health information
   *
   * Modifies an existing health record entry for an individual.
   *
   * @param indId - The ID of the individual
   * @param indHealthId - The ID of the health entry to update
   * @param payload - The updated health data
   * @returns Promise containing the updated health entry
   */
  public updateIndHealth(indId: number, indHealthId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/health/${indHealthId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove health information from an individual
   *
   * Deletes a health record entry from an individual's medical information.
   *
   * @param indId - The ID of the individual
   * @param indHealthId - The ID of the health entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndHealth(indId: number, indHealthId: number) {
    return this.sendRequest(`/individuals/${indId}/health/${indHealthId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create a provider for an individual
   *
   * Adds a new healthcare provider to an individual's provider list.
   *
   * @param indId - The ID of the individual
   * @param payload - The provider data to create
   * @returns Promise containing the created provider entry
   */
  public createIndProvider(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/providers`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update an individual's provider information
   *
   * Modifies an existing healthcare provider entry for an individual.
   *
   * @param indId - The ID of the individual
   * @param indProviderId - The ID of the provider entry to update
   * @param payload - The updated provider data
   * @returns Promise containing the updated provider entry
   */
  public updateIndProvider(indId: number, indProviderId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/providers/${indProviderId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove a provider from an individual
   *
   * Deletes a healthcare provider entry from an individual's provider list.
   *
   * @param indId - The ID of the individual
   * @param indProviderId - The ID of the provider entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndProvider(indId: number, indProviderId: number) {
    return this.sendRequest(`/individuals/${indId}/providers/${indProviderId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Create prescription drug information for an individual
   *
   * Adds a new prescription drug entry to an individual's medication list.
   *
   * @param indId - The ID of the individual
   * @param payload - The prescription drug data to create
   * @returns Promise containing the created prescription entry
   */
  public createIndDrug2(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/rx`, null, { method: 'POST', body: payload });
  }

  /**
   * Update prescription drug information for an individual
   *
   * Modifies an existing prescription drug entry in an individual's medication list.
   *
   * @param indId - The ID of the individual
   * @param indDrugId - The ID of the prescription entry to update
   * @param payload - The updated prescription drug data
   * @returns Promise containing the updated prescription entry
   */
  public updateIndDrug2(indId: number, indDrugId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/rx/${indDrugId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove prescription drug information from an individual
   *
   * Deletes a prescription drug entry from an individual's medication list.
   *
   * @param indId - The ID of the individual
   * @param indDrugId - The ID of the prescription entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndDrug2(indId: number, indDrugId: number) {
    return this.sendRequest(`/individuals/${indId}/rx/${indDrugId}`, null, { method: 'DELETE' });
  }

  /**
   * Create record showing individual takes no drugs
   *
   * Adds a new takes no drug entry
   *
   * @param indId - The ID of the individual
   * @returns Promise containing the created prescription entry
   */
  public createIndDrugNone(indId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/rx/takes-none`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Remove takes no drug information from an individual
   *
   * Deletes the entry from an.
   *
   * @param indId - The ID of the individual
   * @param indNoDrugId - The ID of the prescription entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndDrugNone(indId: number, indNoDrugId: number) {
    return this.sendRequest(`/individuals/${indId}/rx/takes-none/${indNoDrugId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Remove a commission from a user
   *
   * Deletes a commission entry from a user's record.
   *
   * @param userId - The ID of the user
   * @param commissionId - The ID of the commission to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserCommission(userId: number, commissionId: number) {
    return this.sendRequest(`/users/${userId}/commissions/${commissionId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get commission schedules for a user
   *
   * Retrieves commission schedule information for a specific user and commission.
   *
   * @param userId - The ID of the user
   * @param commissionId - The ID of the commission
   * @param options - Additional query parameters
   * @returns Promise containing the commission schedule data
   */
  public getUserCommissionSchedules(userId: number, commissionId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/${commissionId}`, options);
  }

  /**
   * Get commission schedule totals for a user
   *
   * Retrieves aggregated commission totals by type and company for a user's
   * commission schedule. Provides comprehensive financial reporting data.
   *
   * @param userId - The ID of the user
   * @param options - Additional query parameters
   * @returns Promise containing the commission totals breakdown
   */
  public getUserCommissionsScheduleTotals(userId: number, options: object = {}) {
    // return this.sendRequest(`/users/${userId}/commissions/${commissionId}`, options);
    return this.getUserCommissionsSchedule(userId, options).then((commissionSchedule) => {
      const commissionScheduleIssuedTotals = [];
      const commissionScheduleWithPendingTotals = [];
      const commissionScheduleCompanyTotals = [];
      const commissionScheduleTypeTotals = [];
      if (commissionSchedule.types) {
        commissionSchedule.types.forEach((commissionType, commissionTypeKey) => {
          const selectedType = {
            name: commissionType.type,
            values: [],
          };
          commissionScheduleTypeTotals.push(selectedType);
          if (commissionType.companies && commissionType.companies.length > 0) {
            commissionType.companies.forEach((company, companyKey) => {
              let selectedCompany = commissionScheduleCompanyTotals.find(
                (existingCompany) => existingCompany.name === company.name
              );
              if (!selectedCompany) {
                selectedCompany = {
                  name: company.name,
                  values: [],
                };
                commissionScheduleCompanyTotals.push(selectedCompany);
              }
              company.policies.forEach((policy, policyKey) => {
                if (policy.commission && policy.commission.schedule) {
                  policy.commission.schedule.forEach((scheduleMonth, scheduleMonthIndex) => {
                    if (!commissionScheduleIssuedTotals[scheduleMonthIndex]) {
                      commissionScheduleIssuedTotals[scheduleMonthIndex] = 0;
                    }
                    if (!commissionScheduleWithPendingTotals[scheduleMonthIndex]) {
                      commissionScheduleWithPendingTotals[scheduleMonthIndex] = 0;
                    }
                    const parsedValue = parseFloat(scheduleMonth.value);
                    commissionScheduleWithPendingTotals[scheduleMonthIndex] += parsedValue;
                    if (policy.policyStatus === 'Active') {
                      if (!selectedCompany.values[scheduleMonthIndex]) {
                        selectedCompany.values[scheduleMonthIndex] = 0;
                      }
                      if (!selectedType.values[scheduleMonthIndex]) {
                        selectedType.values[scheduleMonthIndex] = 0;
                      }
                      selectedType.values[scheduleMonthIndex] += parsedValue;
                      selectedCompany.values[scheduleMonthIndex] += parsedValue;
                      commissionScheduleIssuedTotals[scheduleMonthIndex] += parsedValue;
                    }
                  });
                }
              });
            });
          }
        });
      }
      const companyTotals = Array.from(commissionScheduleCompanyTotals, (company) => {
        return { name: company.name, total: company.values[0] || 0 };
      }).sort((a, b) => b.total - a.total);
      const typeTotals = Array.from(commissionScheduleTypeTotals, (type) => {
        return { name: type.name, total: type.values[0] || 0 };
      }).sort((a, b) => b.total - a.total);
      return {
        commissionScheduleMonths: commissionSchedule.months,
        commissionScheduleIssuedTotals: commissionScheduleIssuedTotals,
        commissionScheduleWithPendingTotals: commissionScheduleWithPendingTotals,
        commissionScheduleCompanies: Array.from(companyTotals, (company) => company.name),
        commissionScheduleCompanyTotals: Array.from(companyTotals, (company) => company.total),
        commissionScheduleTypes: Array.from(typeTotals, (type) => type.name),
        commissionScheduleTypeTotals: Array.from(typeTotals, (type) => type.total),
      };
    });
  }

  /**
   * Update user commission schedule
   *
   * Modifies an existing commission schedule for a user.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule to update
   * @param payload - Updated schedule configuration data
   * @returns Promise containing the updated schedule details
   */
  public updateUserCommissionSchedule(
    userId: number,
    commissionId: number,
    scheduleId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Create user commission schedule
   *
   * Creates a new commission schedule for a user's commission.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param payload - Commission schedule configuration data
   * @returns Promise containing the created schedule details
   */
  public createUserCommissionSchedule(userId: number, commissionId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions/${commissionId}/schedules`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Remove user commission schedule
   *
   * Permanently deletes a commission schedule from a user's commission.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserCommissionSchedule(userId: number, commissionId: number, scheduleId: number) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get user commission schedule bands
   *
   * Retrieves commission bands for a specific schedule.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule
   * @param options - Additional query parameters
   * @returns Promise containing the schedule bands
   */
  public getUserCommissionScheduleBands(
    userId: number,
    commissionId: number,
    scheduleId,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}`,
      options
    );
  }

  /**
   * Create user commission schedule band
   *
   * Creates a new commission band for a schedule.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule
   * @param payload - Commission band configuration data
   * @returns Promise containing the created band details
   */
  public createUserCommissionScheduleBand(
    userId: number,
    commissionId: number,
    scheduleId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}/bands`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Update user commission schedule band
   *
   * Modifies an existing commission band for a schedule.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule
   * @param bandId - The ID of the band to update
   * @param payload - Updated band configuration data
   * @returns Promise containing the updated band details
   */
  public updateUserCommissionScheduleBand(
    userId: number,
    commissionId: number,
    scheduleId: number,
    bandId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}/bands/${bandId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Remove user commission schedule band
   *
   * Permanently deletes a specific commission band from a schedule.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule
   * @param bandId - The ID of the band to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserCommissionScheduleBand(
    userId: number,
    commissionId: number,
    scheduleId: number,
    bandId: number
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}/bands/${bandId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Remove all user commission schedule bands
   *
   * Permanently deletes all commission bands from a schedule.
   *
   * @param userId - The ID of the user who owns the commission
   * @param commissionId - The ID of the commission
   * @param scheduleId - The ID of the schedule
   * @returns Promise containing the deletion confirmation
   */
  public removeUserCommissionScheduleBands(
    userId: number,
    commissionId: number,
    scheduleId: number
  ) {
    return this.sendRequest(
      `/users/${userId}/commissions/${commissionId}/schedules/${scheduleId}/bands`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get current user notifications
   *
   * Retrieves notifications for the currently authenticated user.
   *
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's notifications
   */
  public getMeNotifications(options: object = {}) {
    return this.sendRequest('/users/me/notifications', options);
  }

  /**
   * Get specific user details
   *
   * Retrieves detailed information about a specific user.
   *
   * @param userId - The ID of the user to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the user details
   */
  public getMe(options: object = {}) {
    return this.sendRequest('/users/me', options);
  }

  /**
   * Get specific user details
   *
   * Retrieves detailed information about a specific user.
   *
   * @param userId - The ID of the user to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the user details
   */
  public getUser(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}`, options);
  }

  /**
   * Get user's agency information
   *
   * Retrieves the agency information associated with a specific user.
   *
   * @param userId - The ID of the user whose agency to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the agency details
   */
  public getUserAgency(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/agency`, options);
  }

  /**
   * Get user's scope
   *
   * Retrieves the scope associated with a specific user.
   *
   * @param userId - The ID of the user whose agency to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the agency details
   */
  public getUserScope(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/scope`, options);
  }

  /**
   * Get user letters
   *
   * Retrieves all letters/templates belonging to a specific user.
   *
   * @param userId - The ID of the user whose letters to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's letters
   */
  public getUserLetters(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/letters`, options);
  }

  /**
   * Get user letters grouped by folder
   *
   * Retrieves user letters organized by folder structure for easier navigation.
   *
   * @param userId - The ID of the user whose letters to retrieve
   * @param options - Query parameters for filtering
   * @returns Promise containing the letters grouped by folder
   */
  public getUserLettersGrouped(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/letters/grouped-by-folder`, options);
  }

  /**
   * Get user shareable letters
   *
   * Retrieves letters that can be shared within an agency.
   *
   * @param userId - The ID of the user requesting the letters
   * @param agencyId - The ID of the agency to get shareable letters from
   * @param options - Query parameters for filtering
   * @returns Promise containing the shareable letters
   */
  public getUserLettersSharable(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/letters-sharable/${agencyId}`, options);
  }

  /**
   * Create user letter
   *
   * Creates a new letter/template for a user.
   *
   * @param userId - The ID of the user to create the letter for
   * @param payload - Letter content and configuration data
   * @returns Promise containing the created letter details
   */
  public createUserLetter(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/letter`, null, { method: 'POST', body: payload });
  }

  /**
   * Update user letter
   *
   * Modifies an existing letter/template for a user.
   *
   * @param userId - The ID of the user who owns the letter
   * @param letterId - The ID of the letter to update
   * @param payload - Updated letter content and configuration
   * @returns Promise containing the updated letter details
   */
  public updateUserLetter(userId: number, letterId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/letter/${letterId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete user letter
   *
   * Permanently removes a letter/template from a user's account.
   *
   * @param userId - The ID of the user who owns the letter
   * @param letterId - The ID of the letter to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserLetter(userId: number, letterId: number) {
    return this.sendRequest(`/users/${userId}/letter/${letterId}`, null, { method: 'DELETE' });
  }

  /**
   * Create user letter sharing
   *
   * Shares a letter/template with other users or agencies.
   *
   * @param userId - The ID of the user who owns the letter
   * @param letterId - The ID of the letter to share
   * @param payload - Sharing configuration including recipients
   * @returns Promise containing the sharing details
   */
  public createUserLetterSharing(userId: number, letterId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/letter-sharing/${letterId}`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Delete user letter sharing
   *
   * Removes a letter sharing configuration, stopping access for shared recipients.
   *
   * @param userId - The ID of the user who owns the letter
   * @param shareId - The ID of the sharing configuration to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserLetterSharing(userId: number, shareId: number) {
    return this.sendRequest(`/users/${userId}/letter-sharing/${shareId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get user individual call logs
   *
   * Retrieves call logs for a specific individual in a user's account.
   *
   * @param userId - The ID of the user who owns the individual
   * @param indId - The ID of the individual to get call logs for
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the individual's call logs
   */
  public getUserIndCallLogs(userId: number, indId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/${indId}/call-logs`, options);
  }

  /**
   * Get user individual SMS thread
   *
   * Retrieves SMS conversation threads for a specific individual.
   *
   * @param userId - The ID of the user who owns the individual
   * @param indId - The ID of the individual to get SMS threads for
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the individual's SMS threads
   */
  public getUserIndSmsThread(userId: number, indId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/individuals/${indId}/sms-threads`, options);
  }

  /**
   * Get user call logs grouped
   *
   * Retrieves call logs organized by grouping criteria for better analysis.
   *
   * @param userId - The ID of the user whose call logs to retrieve
   * @param options - Query parameters including grouping options
   * @returns Promise containing the grouped call logs
   */
  public getUserCallLogsGrouped(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/call-logs/grouped`, options);
  }

  /**
   * Assign call logs by phone number
   *
   * Associates call logs with individuals based on phone number matching.
   *
   * @param userId - The ID of the user performing the assignment
   * @param phone - The phone number to match call logs against
   * @param options - Assignment options and criteria
   * @returns Promise containing the assignment results
   */
  public assignCallLogsByPhone(userId: number, phone: string, options: any = {}) {
    options.phone = phone;
    return this.sendRequest(`/users/${userId}/call-logs/assign`, options, { method: 'POST' });
  }

  /**
   * Get user call logs
   *
   * Retrieves all call logs for a specific user.
   *
   * @param userId - The ID of the user whose call logs to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's call logs
   */
  public getUserCallLogs(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/call-logs`, options);
  }

  /**
   * Get user call log recording
   *
   * Retrieves the recording file or URL for a specific call log entry.
   *
   * @param userId - The ID of the user who owns the call log
   * @param indId - The ID of the individual associated with the call
   * @param callLogId - The ID of the call log entry
   * @param options - Additional query parameters
   * @returns Promise containing the call recording details
   */
  public getUserCallLogRecording(
    userId: number,
    indId: number,
    callLogId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/individuals/${indId}/call-logs/${callLogId}/recording`,
      options
    );
  }

  /**
   * Update user individual call log
   *
   * Modifies an existing call log entry with new information or notes.
   *
   * @param userId - The ID of the user who owns the call log
   * @param id - The ID of the call log entry to update
   * @param payload - Updated call log data
   * @returns Promise containing the updated call log details
   */
  public updateUserIndCallLog(userId: number, id: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/call-logs/${id}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getUserSavedReports(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/saved-reports`, options);
  }

  /**
   * Create user saved report
   *
   * Creates a new saved report configuration for a user.
   *
   * @param userId - The ID of the user to create the saved report for
   * @param payload - Saved report configuration data
   * @returns Promise containing the created saved report details
   */
  public createUserSavedReport(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/saved-reports`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Remove user saved report
   *
   * Permanently deletes a saved report configuration from a user's account.
   *
   * @param userId - The ID of the user who owns the saved report
   * @param savedReportId - The ID of the saved report to remove
   * @param payload - Additional deletion parameters
   * @returns Promise containing the deletion confirmation
   */
  public removeUserSavedReport(userId: number, savedReportId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/saved-reports/${savedReportId}`, null, {
      method: 'DELETE',
      body: payload,
    });
  }

  /**
   * Get user flags
   *
   * Retrieves all flags available to or used by a specific user.
   *
   * @param userId - The ID of the user whose flags to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's flags
   */
  public getUserFlags(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/flags`, options);
  }

  /**
   * Get user flag by color
   *
   * Retrieves a specific flag based on its color identifier.
   *
   * @param userId - The ID of the user who owns the flag
   * @param flagColor - The color identifier of the flag to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the flag details
   */
  public getUserFlagByColor(userId: number, flagColor: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/flags/by-color/${flagColor}`, options);
  }

  /**
   * Get user documents
   *
   * Retrieves documents of a specific type for a user.
   *
   * @param userId - The ID of the user whose documents to retrieve
   * @param type - The type of documents to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's documents
   */
  public getUserDocuments(userId: number, type: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/documents/${type}`, options);
  }

  /**
   * Get user document by system name
   *
   * Retrieves a specific document using its system name identifier.
   *
   * @param userId - The ID of the user who owns the document
   * @param docSystemName - The system name of the document to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the document details
   */
  public getUserDocumentBySystemName(userId: number, docSystemName: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/document-by-system-name/${docSystemName}`, options);
  }

  /**
   * Create user email batch
   *
   * Creates a new email batch for sending bulk emails to multiple recipients.
   *
   * @param userId - The ID of the user creating the email batch
   * @param payload - Email batch configuration including recipients and content
   * @returns Promise containing the created email batch details
   */
  public createUserEmailBatch(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/email-batches`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Create user VoIP extensions
   *
   * Creates VoIP extension configurations for a user's phone system.
   *
   * @param userId - The ID of the user to create extensions for
   * @param payload - VoIP extension configuration data
   * @returns Promise containing the created extension details
   */
  public createUserVoipExtensions(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-extensions`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Create user VoIP phone numbers
   *
   * Creates VoIP phone number configurations for a user's account.
   *
   * @param userId - The ID of the user to create phone numbers for
   * @param payload - VoIP phone number configuration data
   * @returns Promise containing the created phone number details
   */
  public createUserVoipPhoneNumbers(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-phone-numbers`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Delete user VoIP extensions
   *
   * Removes VoIP extension configurations from a user's account.
   *
   * @param userId - The ID of the user who owns the extensions
   * @param providerId - The ID of the VoIP provider
   * @param payload - Deletion parameters
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserVoipExtensions(userId: number, providerId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-extensions/${providerId}`, null, {
      method: 'DELETE',
      body: payload,
    });
  }

  /**
   * Delete user VoIP phone numbers
   *
   * Removes VoIP phone number configurations from a user's account.
   *
   * @param userId - The ID of the user who owns the phone numbers
   * @param providerId - The ID of the VoIP provider
   * @param payload - Deletion parameters
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserVoipPhoneNumbers(userId: number, providerId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/voip-phone-numbers/${providerId}`, null, {
      method: 'DELETE',
      body: payload,
    });
  }

  /**
   * Get users automation access
   *
   * Retrieves automation access permissions and capabilities for a user.
   *
   * @param userId - The ID of the user whose automation access to check
   * @param options - Additional query parameters
   * @returns Promise containing the user's automation access details
   */
  public getUsersAutomationAccess(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/automations-access`, options);
  }

  /**
   * Check if user is agency principal
   *
   * Determines whether a user has principal status within their agency.
   *
   * @param userId - The ID of the user to check
   * @param options - Additional query parameters
   * @returns Promise containing the principal status result
   */
  public getUserIsAgencyPrincipal(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/is-principal`, options);
  }

  /**
   * Check if user is LOA agency principal
   *
   * Determines whether a user has Letter of Authorization (LOA) principal status.
   *
   * @param userId - The ID of the user to check
   * @param options - Additional query parameters
   * @returns Promise containing the LOA principal status result
   */
  public getUserIsLoaAgencyPrincipal(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/is-loa-principal`, options);
  }

  /**
   * Check if user is agency member
   *
   * Determines whether a user is a member of an agency.
   *
   * @param userId - The ID of the user to check
   * @param options - Additional query parameters
   * @returns Promise containing the agency membership status
   */
  public getUserIsAgencyMember(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/is-agency-member`, options);
  }

  /**
   * Get user email batch
   *
   * Retrieves email batch configurations and status for a user.
   *
   * @param userId - The ID of the user whose email batches to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's email batches
   */
  public getUserEmailBatch(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/email-batches`, options);
  }

  /**
   * Remove user email batch
   *
   * Permanently deletes an email batch configuration and stops its processing.
   *
   * @param userId - The ID of the user who owns the email batch
   * @param batchId - The ID of the email batch to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserEmailBatch(userId: number, batchId: number) {
    return this.sendRequest(`/users/${userId}/email-batches/${batchId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get user email batch queue
   *
   * Retrieves the queue of emails pending in a specific batch.
   *
   * @param userId - The ID of the user who owns the email batch
   * @param batchId - The ID of the email batch
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the email batch queue
   */
  public getUserEmailBatchQueue(userId: number, batchId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/email-batches/${batchId}/queue`, options);
  }

  /**
   * Create user email batch queue
   *
   * Adds emails to the queue of a specific email batch for processing.
   *
   * @param userId - The ID of the user who owns the email batch
   * @param userEmailBatch - The ID of the email batch
   * @param payload - Array of email recipients and data to add to the queue
   * @returns Promise containing the queue creation results
   */
  public createUserEmailBatchQueue(userId: number, userEmailBatch: number, payload: object = []) {
    return this.sendRequest(`/users/${userId}/email-batches/${userEmailBatch}/queue`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Create individuals notes (batch)
   *
   * Creates notes for multiple individuals in a single operation.
   *
   * @param payload - Array of note data for multiple individuals
   * @returns Promise containing the batch note creation results
   */
  public createIndsNotes(payload: object = []) {
    return this.sendRequest('/individuals/notes', null, { method: 'POST', body: payload });
  }

  /**
   * Create individual note
   *
   * Creates a new note for a specific individual.
   *
   * @param indId - The ID of the individual to create a note for
   * @param payload - Note content and metadata
   * @returns Promise containing the created note details
   */
  public createIndNote(indId: number, payload: object = []) {
    return this.sendRequest(`/individuals/${indId}/notes`, null, { method: 'POST', body: payload });
  }

  /**
   * Update individual note
   *
   * Modifies an existing note for an individual.
   *
   * @param indId - The ID of the individual who owns the note
   * @param indNoteId - The ID of the note to update
   * @param payload - Updated note content and metadata
   * @returns Promise containing the updated note details
   */
  public updateIndNote(indId: number, indNoteId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/notes/${indNoteId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Create individual pharmacy
   *
   * Adds a pharmacy to an individual's record.
   *
   * @param indId - The ID of the individual to add pharmacy for
   * @param payload - Pharmacy information including name, address, and contact details
   * @returns Promise containing the created pharmacy details
   */
  public createIndPharmacy(indId: number, payload: object = []) {
    return this.sendRequest(`/individuals/${indId}/pharmacies`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update individual pharmacy
   *
   * Modifies pharmacy information in an individual's record.
   *
   * @param indId - The ID of the individual who owns the pharmacy record
   * @param indPharmacyId - The ID of the pharmacy record to update
   * @param payload - Updated pharmacy information
   * @returns Promise containing the updated pharmacy details
   */
  public updateIndPharmacy(indId: number, indPharmacyId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${indId}/pharmacies/${indPharmacyId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove individual pharmacy
   *
   * Permanently deletes a pharmacy record from an individual's account.
   *
   * @param indId - The ID of the individual who owns the pharmacy record
   * @param indPharmacyId - The ID of the pharmacy record to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeIndPharmacy(indId: number, indPharmacyId: number) {
    return this.sendRequest(`/individuals/${indId}/pharmacies/${indPharmacyId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get spreadsheet metadata
   *
   * Retrieves metadata information about a spreadsheet file, including
   * available sheets and their properties. Useful for file preview and processing.
   *
   * @param key - The file key identifier
   * @param bucket - The storage bucket containing the file
   * @returns Promise containing the spreadsheet metadata
   */
  public getSpreadsheetMetadata(key: string, bucket: string): Promise<SpreadsheetMetadata> {
    const options = {
      key: key,
      bucket: bucket,
    };
    return this.sendRequest('/utilities/files/spreadsheets', options).then((response) => {
      return response as SpreadsheetMetadata;
    });
  }

  /**
   * Upload a file to the server
   *
   * Uploads a file using FormData and returns the file information including
   * the generated key for future reference.
   *
   * @param fileData - The FormData containing the file to upload
   * @param options - Additional upload options
   * @returns Promise containing the uploaded file information
   */
  public uploadFile(fileData: FormData, options: object = {}) {
    return this.sendRequest('/utilities/upload/file', options, {
      method: 'POST',
      body: fileData,
      bodyEncode: false,
    }).then((response) => {
      return response.file as FileUploadResponse;
    });
  }

  /**
   * Upload a file to CDN
   *
   * Uploads a file directly to the Content Delivery Network for faster access.
   *
   * @param fileData - The file data to upload
   * @returns Promise containing the upload response
   */
  public uploadFileCdn(fileData: any) {
    return this.sendRequest('/utilities/upload/file/cdn', null, {
      method: 'POST',
      body: fileData,
      bodyEncode: false,
    });
  }

  /**
   * Send an email message
   *
   * Sends an email using the system's email service integration.
   *
   * @param sendData - The email data including recipients, subject, and content
   * @returns Promise containing the send confirmation
   */
  public sendEmail(sendData: any) {
    return this.sendRequest('/utilities/send/email', null, { method: 'POST', body: sendData });
  }

  /**
   * Send an SMS message
   *
   * Sends an SMS message using the system's SMS service integration.
   *
   * @param sendData - The SMS data including recipient and message content
   * @returns Promise containing the send confirmation
   */
  public sendSMS(sendData: any) {
    return this.sendRequest('/utilities/send/sms', null, { method: 'POST', body: sendData });
  }

  /**
   * Send multi-factor authentication code
   *
   * Sends an MFA code to the user for authentication purposes.
   *
   * @param sendData - The MFA code delivery data
   * @returns Promise containing the send confirmation
   */
  public sendMFACode(sendData: any) {
    return this.sendRequest('/utilities/mfa/send-code', null, { method: 'POST', body: sendData });
  }

  /**
   * Check multi-factor authentication code
   *
   * Validates an MFA code provided by the user for authentication.
   *
   * @param sendData - The MFA code verification data
   * @returns Promise containing the validation result
   */
  public checkMFACode(sendData: any) {
    return this.sendRequest('/utilities/mfa/check-code', null, { method: 'POST', body: sendData });
  }

  /**
   * Get drug auto-complete suggestions from Connecture
   *
   * Retrieves drug name suggestions from the Connecture integration
   * based on partial drug name input.
   *
   * @param drugName - Partial or complete drug name to search for
   * @returns Promise containing the list of matching drug suggestions
   */
  public connectureDrugAutoComplete(drugName: string) {
    return this.sendRequest('/integrations/connecture/drugs/auto-complete', { drugName: drugName });
  }

  /**
   * Get drug dosages from Connecture
   *
   * Retrieves available dosages for a specific drug from the Connecture integration.
   *
   * @param drugId - The unique identifier for the drug
   * @returns Promise containing the list of available dosages
   */
  public connectureDrugDosages(drugId: string) {
    return this.sendRequest(`/integrations/connecture/drugs/${drugId}/dosages`, {});
  }

  /**
   * Get RXCUI by NDC from Connecture
   *
   * Retrieves the RxNorm Concept Unique Identifier (RXCUI) for a drug
   * using its National Drug Code (NDC).
   *
   * @param ndc - The National Drug Code to look up
   * @returns Promise containing the RXCUI information
   */
  public connectureDrugRxcuiByNdc(ndc: string) {
    return this.sendRequest(`/integrations/connecture/drugs/${ndc}/get-rxcui`, {});
  }

  /**
   * Search for pharmacies using Connecture
   *
   * Searches for pharmacies within a specified radius of a ZIP code
   * using the Connecture integration.
   *
   * @param zip - The ZIP code to search around
   * @param radius - The search radius in miles
   * @param name - Optional pharmacy name filter
   * @returns Promise containing the list of matching pharmacies
   */
  public connecturePharmacySearch(zip: string, radius: number, name: any) {
    return this.sendRequest('/integrations/connecture/pharmacies/search', {
      zip: zip,
      radius: radius,
      name: name,
    });
  }

  /**
   * Get a signed URL for file upload
   *
   * Generates a pre-signed URL that can be used to upload files directly
   * to cloud storage without exposing credentials.
   *
   * @param key - The file key/path for the upload
   * @param userId - The ID of the user performing the upload
   * @param bucket - Optional bucket name (uses default if not provided)
   * @param disposition - How the file should be handled (attachment/inline)
   * @returns Promise containing the signed URL and upload parameters
   */
  public uploadSignUrl(
    key: any,
    userId: number,
    bucket: string = null,
    disposition = 'attachment'
  ) {
    return this.sendRequest('/utilities/upload/signed-url', {
      key: key,
      userId: userId,
      bucket: bucket,
      disposition: disposition,
    });
  }

  /**
   * OAuth login authentication
   *
   * Authenticates a user using OAuth flow and returns auth code.
   *
   * @param payload - The OAuth login data including provider information
   * @returns Promise containing the auth code
   */
  public oAuthLogin(payload: oAuthLoginPayload): Promise<oAuthLoginResponse> {
    return this.sendRequest('/oauth/login', null, { method: 'POST', body: payload });
  }

  /**
   * OAuth login complete
   *
   * Authenticates a user using OAuth flow and returns access token.
   *
   * @param payload - The OAuth login data including provider information
   * @returns Promise containing the authentication token
   */
  public oAuthGetToken(payload: oAuthGetTokenPayload): Promise<oAuthGetTokenResponse> {
    return this.sendRequest('/oauth/token', null, { method: 'POST', body: payload });
  }

  /**
   * OAuth token refresh
   *
   */
  public oAuthRefreshToken(payload: oAuthRefreshTokenPayload): Promise<oAuthRefreshTokenResponse> {
    return this.sendRequest('/oauth/token', null, { method: 'POST', body: payload });
  }

  /**
   * OAuth log out
   *
   */
  public oAuthLogOut() {
    return this.sendRequest('/oauth/logout', null, { method: 'POST' });
  }

  /**
   * Get Auth0 SAML payload
   *
   * Retrieves SAML authentication payload for Auth0 integration.
   *
   * @returns Promise containing the SAML payload data
   */
  public getAuth0SAMLPayload() {
    return this.sendRequest('/auth0/saml/payload');
  }

  /**
   * Get Auth0 SAML payload for individual
   *
   * Retrieves individual-specific SAML authentication payload for Auth0 integration.
   *
   * @param indId - The ID of the individual
   * @param payload - Additional payload data for the SAML request
   * @returns Promise containing the individual SAML payload
   */
  public getAuth0SAMLIndPayload(indId: number, payload: object) {
    return this.sendRequest(`/auth0/saml/individual/${indId}/payload`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public getGlobUserTemplates(options: object = {}) {
    return this.sendRequest('/glob-templates', options);
  }
  public disconnectUserIntegrationOAuth(providerReference: string, options: object = {}) {
    return this.sendRequest(`/integrations/oauth/${providerReference}/deauthorize`, options, {
      method: 'POST',
    });
  }
  public validateUserIntegrationOAuth(providerReference: string, options: object = {}) {
    return this.sendRequest(`/integrations/oauth/${providerReference}/me`, options);
  }
  public getUserIntegrationMailThreads(
    userId: number,
    providerReference: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/mail/threads`,
      options
    );
  }
  public getUserIntegrationMail(userId: number, providerReference: string, options: object = {}) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/${providerReference}/mail/messages`,
      options
    );
  }
  public getUserIntegrationAWeberLists(userId: number, accountId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/aweber/${accountId}/lists`);
  }
  public getUserIntegrationConstantContactLists(userId: number, accountId: number) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/constantcontact/${accountId}/lists`
    );
  }
  public addUserIntegrationConstantContactContact(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/constantcontact/contact`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public addUserIntegrationConstantContactContacts(userId: number, payload: object) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/constantcontact/contact-bulk`,
      null,
      { method: 'POST', body: payload }
    );
  }
  public addUserIntegrationMediZuesContacts(payload) {
    return this.sendRequest('/integrations/pat/medizues/contacts', null, {
      method: 'POST',
      body: payload,
    });
  }
  public addUserIntegrationEnrollHereContacts(payload) {
    return this.sendRequest('/integrations/pat/enrollhere/contacts', null, {
      method: 'POST',
      body: payload,
    });
  }
  public getUserIntegrationExternalAccounts(service: string, token: string) {
    return this.sendRequest(`/integrations/pat/${service}/accounts/${token}`);
  }
  public getUserIntegrationConstantContactContacts(userId: number, lists: string) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/constantcontact/contacts/${lists}`
    );
  }
  public getUserIntegrationConstantContactActivity(userId: number, activityId: number) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/constantcontact/activity/${activityId}`
    );
  }
  public getUserIntegrationMailChimpLists(userId: number, accountId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/mailchimp/${accountId}/lists`);
  }
  public addUserIntegrationMailChimpContacts(userId: number, payload: object) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/mailchimp/add-contacts-bulk`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get user integration MailboxPower groups
   *
   * Retrieves contact groups from a user's MailboxPower integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @param accountId - The MailboxPower account ID
   * @returns Promise containing the list of contact groups
   */
  /**
   * Get user integration MailboxPower groups
   *
   * Retrieves available contact groups from a user's MailboxPower integration account.
   * Groups are used to organize and categorize contacts for targeted messaging.
   *
   * @param userId - The ID of the user who owns the integration
   * @param accountId - The MailboxPower account ID
   * @returns Promise containing the list of contact groups
   */
  public getUserIntegrationMailboxPowerGroups(userId: number, accountId: number) {
    return this.sendRequest(`/users/${userId}/integrations/pat/mailboxpower/${accountId}/groups`);
  }

  /**
   * Add user integration MailboxPower contacts
   *
   * Adds contacts to a MailboxPower integration account.
   *
   * @param userId - The ID of the user who owns the integration
   * @param accountId - The MailboxPower account ID
   * @param payload - Contact data to add to MailboxPower
   * @returns Promise containing the contact addition results
   */
  public addUserIntegrationMailboxPowerContacts(
    userId: number,
    accountId: string,
    payload: object
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/pat/mailboxpower/${accountId}/contacts`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get user integration SlyBroadcast audio files
   *
   * Retrieves available audio files from a user's SlyBroadcast integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of available audio files
   */
  public getUserIntegrationSlyBroadcastAudioFiles(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/pat/slybroadcast/audio-files`);
  }

  /**
   * Send user integration SlyBroadcast voicemail
   *
   * Sends voicemail messages through the SlyBroadcast integration.
   *
   * @param userId - The ID of the user sending the voicemail
   * @param payload - Voicemail data including recipients and audio file
   * @returns Promise containing the voicemail send results
   */
  public sendUserIntegrationSlyBroadcastVoicemail(userId: number, payload: object) {
    return this.sendRequest(
      `/users/${userId}/integrations/pat/slybroadcast/send-voicemails`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Get user integration contact sync lists
   *
   * Retrieves contact synchronization lists for a specific integration provider.
   *
   * @param reference - The reference identifier of the integration provider
   * @returns Promise containing the list of contact sync lists
   */
  public getUserIntegrationContactSyncLists(reference: string) {
    return this.sendRequest(`/users/integrations/${reference}/contact-sync-lists`);
  }

  /**
   * Get user integration Thanks.io gift cards
   *
   * Retrieves available gift card options from a user's Thanks.io integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of available gift cards
   */
  public getUserIntegrationThanksioGiftcards(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/giftcards`);
  }

  /**
   * Get user integration Thanks.io handwriting styles
   *
   * Retrieves available handwriting styles for Thanks.io card personalization.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of handwriting styles
   */
  public getUserIntegrationThanksioHandwritingStyles(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/handwriting-styles`);
  }

  /**
   * Get user integration Thanks.io image templates
   *
   * Retrieves available image templates for Thanks.io cards and mailings.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of image templates
   */
  public getUserIntegrationThanksioImageTemplates(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/image-templates`);
  }

  /**
   * Get user integration Thanks.io image template
   *
   * Retrieves a specific image template by ID from Thanks.io integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @param templateId - The ID of the template to retrieve
   * @returns Promise containing the image template details
   */
  public getUserIntegrationThanksioImageTemplate(userId: number, templateId: number) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/thanksio/image-templates/${templateId}`
    );
  }

  /**
   * Get user integration Thanks.io message templates
   *
   * Retrieves available message templates for Thanks.io communications.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of message templates
   */
  public getUserIntegrationThanksioMessageTemplates(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/message-templates`);
  }

  /**
   * Get user integration Thanks.io message template
   *
   * Retrieves a specific message template by ID from Thanks.io integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @param templateId - The ID of the template to retrieve
   * @returns Promise containing the message template details
   */
  public getUserIntegrationThanksioMessageTemplate(userId: number, templateId: number) {
    return this.sendRequest(
      `/users/${userId}/integrations/oauth/thanksio/message-templates/${templateId}`
    );
  }

  /**
   * Send user integration Thanks.io mail
   *
   * Sends physical mail (postcards, letters) through the Thanks.io integration.
   *
   * @param userId - The ID of the user sending the mail
   * @param mailType - The type of mail to send (postcard, letter, etc.)
   * @param payload - Mail data including recipient, template, and personalization
   * @returns Promise containing the mail send results
   */
  public sendUserIntegrationThanksioSend(userId: number, mailType: string, payload: object) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/send/${mailType}`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user integration Thanks.io mailing lists
   *
   * Retrieves mailing lists from a user's Thanks.io integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of mailing lists
   */
  public getUserIntegrationThanksioMailingLists(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/mailing-lists`);
  }

  /**
   * Add user integration Thanks.io contacts
   *
   * Adds contacts to a user's Thanks.io integration for mailing purposes.
   *
   * @param userId - The ID of the user who owns the integration
   * @param payload - Contact data to add to Thanks.io
   * @returns Promise containing the contact addition results
   */
  public addUserIntegrationThanksioContacts(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/integrations/oauth/thanksio/add-contacts`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user integration DirectMailManager artwork
   *
   * Retrieves a specific artwork template from DirectMailManager integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @param artworkId - The ID of the artwork to retrieve
   * @returns Promise containing the artwork details
   */
  public getUserIntegrationDirectMailManagerArtwork(userId: number, artworkId: string) {
    return this.sendRequest(
      `/users/${userId}/integrations/pat/directmailmanager/artwork/${artworkId}`
    );
  }

  /**
   * Get user integration DirectMailManager artworks
   *
   * Retrieves all available artwork templates from DirectMailManager integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of available artworks
   */
  public getUserIntegrationDirectMailManagerArtworks(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/pat/directmailmanager/artwork`);
  }

  /**
   * Send user integration DirectMailManager postcard
   *
   * Sends a single postcard through the DirectMailManager integration.
   *
   * @param userId - The ID of the user sending the postcard
   * @param payload - Postcard data including recipient, artwork, and message
   * @returns Promise containing the postcard send results
   */
  public sendUserIntegrationDirectMailManagerPostcard(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/integrations/pat/directmailmanager/postcard`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Send user integration DirectMailManager postcards (bulk)
   *
   * Sends multiple postcards in bulk through the DirectMailManager integration.
   *
   * @param userId - The ID of the user sending the postcards
   * @param payload - Array of postcard data for bulk sending
   * @returns Promise containing the bulk postcard send results
   */
  public sendUserIntegrationDirectMailManagerPostcards(userId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/integrations/pat/directmailmanager/postcards`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user integration DirectMailManager mailing lists
   *
   * Retrieves mailing lists from DirectMailManager integration.
   *
   * @param userId - The ID of the user who owns the integration
   * @returns Promise containing the list of mailing lists
   */
  public getUserIntegrationDirectMailManagerMailingLists(userId: number) {
    return this.sendRequest(`/users/${userId}/integrations/pat/directmailmanager/mailing-lists`);
  }

  /**
   * Add user integration DirectMailManager contacts
   *
   * Adds contacts to DirectMailManager integration for mailing purposes.
   *
   * @param userId - The ID of the user who owns the integration
   * @param payload - Contact data to add to DirectMailManager
   * @returns Promise containing the contact addition results
   */
  public addUserIntegrationDirectMailManagerContacts(userId: number, payload: object) {
    return this.sendRequest(
      `/users/${userId}/integrations/pat/directmailmanager/add-contacts`,
      null,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Delete user integration contact from list
   *
   * Removes a contact from a specific mailing list in an integration provider.
   *
   * @param userId - The ID of the user who owns the integration
   * @param contactId - The ID of the contact to remove
   * @param listId - The ID of the mailing list
   * @param provider - The provider name
   * @param providerType - The type of provider (oauth, pat, etc.)
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserIntegrationContactsOnList(
    userId: number,
    contactId: number,
    listId: number,
    provider: string,
    providerType: string
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/${providerType}/${provider}/contacts/${contactId}/list/${listId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Save user integration mail attachment
   *
   * Saves an email attachment from an integrated mail provider to the system.
   *
   * @param providerReference - The reference identifier of the mail provider
   * @param messageId - The ID of the email message containing the attachment
   * @param attachmentId - The ID of the attachment to save
   * @param options - Additional save options
   * @returns Promise containing the attachment save results
   */
  public saveUserIntegrationMailAttachment(
    providerReference: string,
    messageId: string,
    attachmentId: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/integrations/oauth/${providerReference}/mail/messages/${messageId}/attachments/${attachmentId}/save`,
      options,
      { method: 'POST' }
    );
  }

  /**
   * Create new user integration mail
   *
   * Sends a new email message through an integrated mail provider.
   *
   * @param providerReference - The reference identifier of the mail provider
   * @param options - Additional mail options
   * @param payload - Email data including recipients, subject, and content
   * @returns Promise containing the email send results
   */
  public newUserIntegrationMail(
    providerReference: string,
    options: object = {},
    payload: object = {}
  ) {
    return this.sendRequest(`/integrations/oauth/${providerReference}/mail/messages`, options, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Reply to user integration mail
   *
   * Sends a reply to an email message through an integrated mail provider.
   *
   * @param providerReference - The reference identifier of the mail provider
   * @param messageId - The ID of the message to reply to
   * @param options - Additional reply options
   * @param payload - Reply data including content and recipients
   * @returns Promise containing the reply send results
   */
  public replyUserIntegrationMail(
    providerReference: string,
    messageId: string,
    options: object = {},
    payload: object = {}
  ) {
    return this.sendRequest(
      `/integrations/oauth/${providerReference}/mail/messages/${messageId}/reply`,
      options,
      { method: 'POST', body: payload }
    );
  }

  /**
   * Delete user integration contacts
   *
   * Removes a contact from an integration provider.
   *
   * @param userId - The ID of the user who owns the integration
   * @param contactId - The ID of the contact to delete
   * @param provider - The provider name
   * @param providerType - The type of provider (oauth, pat, etc.)
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserIntegrationContacts(
    userId: number,
    contactId: number,
    provider: string,
    providerType: string
  ) {
    return this.sendRequest(
      `/users/${userId}/integrations/${providerType}/${provider}/contacts/${contactId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get user policies commission by phase
   *
   * Retrieves commission data for user policies filtered by their current phase.
   *
   * @param userId - The ID of the user whose commissions to retrieve
   * @param policyPhase - The phase of policies to filter by (issued, pending, etc.)
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the commission data by phase
   */
  public getUserPoliciesCommission(userId: number, policyPhase: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/commissions-by-phase/${policyPhase}`, options);
  }

  /**
   * Get user policy commission
   *
   * Retrieves commission details for a specific policy and phase.
   *
   * @param userId - The ID of the user who owns the policy
   * @param policyId - The ID of the policy
   * @param policyPhase - The phase of the policy (issued, pending, etc.)
   * @param options - Additional query parameters
   * @returns Promise containing the policy commission details
   */
  public getUserPolicyCommission(
    userId: number,
    policyId: number,
    policyPhase: string,
    options: object = {}
  ) {
    return this.sendRequest(`/users/${userId}/commission/${policyId}/${policyPhase}`, options);
  }

  /**
   * Get user leads statuses
   *
   * Retrieves all lead status configurations and data for a user.
   *
   * @param userId - The ID of the user whose lead statuses to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's lead statuses
   */
  public getUserLeadsStatuses(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/leads/statuses`, options);
  }

  /**
   * Update agencies settings
   *
   * Modifies an existing agencies settings.
   *
   * @param userId - The ID of the user who is affiliated with the agency
   * @param agencyId - The ID of the agency
   * @param payload - Updated settings data
   * @returns Promise containing the updated setting
   */
  public updateAgencySetting(agencyId: number, payload: object = {}) {
    return this.sendRequest(`/agencies/${agencyId}/setting`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getUserActionSuggestions(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/ai-suggestions`, options);
  }
  public updateUserActionSuggestion(userId: number, suggestionId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/ai-suggestions/${suggestionId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getUserActionSuggestionsCountBy(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/ai-suggestions/county-by`, options);
  }
  public getUserActivitiesCategories(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/activities/categories`, options);
  }
  public getUserActivitiesSavedCategories(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/activities/saved-categories`, options);
  }
  public addUserActivity(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/activities`, null, { method: 'POST', body: payload });
  }
  public addUserActivityCategory(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/activities/categories`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public updateUserActivityCategory(userId: number, categoryId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/activities/categories/${categoryId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public removeUserActivityCategory(userId: number, categoryId: number) {
    return this.sendRequest(`/users/${userId}/activities/categories/${categoryId}`, null, {
      method: 'DELETE',
    });
  }
  public removeUserRecent(userId: number, individualId: number) {
    return this.sendRequest(`/users/${userId}/individual/${individualId}/recent`, null, {
      method: 'DELETE',
    });
  }
  public updateUserRecent(userId: number, individualId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/individual/${individualId}/recent`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getAgencyConnecture(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/connecture/`, options);
  }
  public updateAgencyConnecture(userId: number, agencyId: number, urlId: number, payload: any[]) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/connecture/${urlId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public deleteAgencyConnecture(userId: number, agencyId: number, urlId: number) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/connecture/${urlId}`, null, {
      method: 'DELETE',
    });
  }
  public addAgencyConnecture(userId: number, agencyId: number, payload: object) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/connecture`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public getAgencyReportUploads(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/agency/${agencyId}/report-uploads`, options);
  }
  public createAgencyReportUpload(userId: number, agencyId: number, fileData: any) {
    return this.sendRequest(`/users/${userId}/agency/${agencyId}/report-uploads`, null, {
      method: 'POST',
      body: fileData,
      bodyEncode: false,
    });
  }
  public updateAgencyReportUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    payload: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/report-uploads/${reportId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }
  public extractAgencyAgentUpload(userId: number, agencyId: number, reportId: number) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/report-uploads/${reportId}/extract`
    );
  }
  public processAgencyAgentContractRtsFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/process-rts-from-upload/${reportId}`,
      options
    );
  }
  public processAgencyAgentContractChunk(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/process-contracts-from-upload/${reportId}`,
      options
    );
  }
  public processAgencyAgentContractStatesFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/process-contract-states-from-upload/${reportId}`,
      options
    );
  }
  public processAgencyAgentContractProductsFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/process-contract-products-from-upload/${reportId}`,
      options
    );
  }
  public processAgencyAgentContractWritingNumbersFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/process-contract-writing-numbers-from-upload/${reportId}`,
      options
    );
  }
  public findAgencyAgentContractMissingFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/contracts/find-missing-contracts-from-upload/${reportId}`,
      options
    );
  }
  public downloadAgencyAgentContractMissingFromUpload(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    if (this.accessToken) {
      options['accessToken'] = this.accessToken;
    }
    return `${this.baseUrl}/users/${userId}/agency/${agencyId}/report-uploads/${reportId}/download-missing-contracts?${this.buildQuery(options)}`;
  }
  public exportAgencyConnectureBrokerListUrl(
    userId: number,
    agencyId: number,
    options: object = {}
  ) {
    if (this.accessToken) {
      options['accessToken'] = this.accessToken;
    }
    return `${this.baseUrl}/user/${userId}/agency/${agencyId}/connecture-broker-list?${this.buildQuery(options)}`;
  }
  public getAgencyDownlines(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/downlines/`, options);
  }
  public getAgencyLoasAndPrincipal(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(
      `/user/${userId}/agency/${agencyId}/downline-loa-and-principal/`,
      options
    );
  }
  public getAgencyLoas(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/downline-loa/`, options);
  }
  public getAgencyDownlineAgencies(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}/downline-agencies/`, options);
  }
  public getAgencyPackageSeatsAvailable(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(
      `/user/${userId}/agency/${agencyId}/agency-package-seats-available`,
      options
    );
  }
  public updateUser(payload: any[]) {
    return this.sendRequest('/users', null, { method: 'PATCH', body: payload });
  }
  public getAgency(userId: number, agencyId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}`, options);
  }
  public updateAgency(userId: number, agencyId: number, payload: object = {}) {
    return this.sendRequest(`/user/${userId}/agency/${agencyId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public getGlobConnectureOrgs(options: object = {}) {
    return this.sendRequest('/connecture-organizations');
  }
  public validateUserConnecture(reference: string, payload: object) {
    return this.sendRequest(`/connecture-organizations/validate/${reference}`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public saveIntegrationSingleAgentConnecture(reference: string, payload: object) {
    return this.sendRequest(`/integrations/${reference}/connect-single-agent-connecture`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public removeIntegrationSingleAgentConnecture(reference: string, payload: object) {
    return this.sendRequest(`/integrations/${reference}/disable-single-agent-connecture`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public getGlobSunfireOrgs(options: object = {}) {
    return this.sendRequest('/sunfire-organizations');
  }
  public getUserSunfire(userId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/sunfireMatrix`, options);
  }
  public updateUserSunfire(userId: number, payload: any[]) {
    return this.sendRequest(`/user/${userId}/sunfireMatrix`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Add SunFire integration for user
   *
   * Creates a new SunFire matrix integration configuration for the specified user.
   *
   * @param userId - The ID of the user to add SunFire integration for
   * @param payload - The SunFire configuration data
   * @returns Promise containing the created integration details
   */
  public addUserSunfire(userId: number, payload: object) {
    return this.sendRequest(`/user/${userId}/sunfireMatrix`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Delete SunFire integration for user
   *
   * Permanently removes a SunFire integration configuration from a user's account.
   *
   * @param userId - The ID of the user who owns the integration
   * @param userSunfireId - The ID of the SunFire integration to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserSunfire(userId: number, userSunfireId: number) {
    return this.sendRequest(`/user/${userId}/sunfireMatrix/${userSunfireId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get user Connecture integrations
   *
   * Retrieves all Connecture integration configurations for the specified user.
   *
   * @param userId - The ID of the user whose Connecture integrations to retrieve
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the list of Connecture integrations
   */
  public getUserConnecture(userId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/connecture`, options);
  }

  /**
   * Update user Connecture integration
   *
   * Modifies an existing Connecture integration configuration.
   *
   * @param userId - The ID of the user who owns the integration
   * @param id - The ID of the Connecture integration to update
   * @param payload - The updated integration configuration
   * @returns Promise containing the updated integration details
   */
  public updateUserConnecture(userId: number, id: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/connecture/${id}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete user Connecture integration
   *
   * Permanently removes a Connecture integration from a user's account.
   *
   * @param userId - The ID of the user who owns the integration
   * @param id - The ID of the Connecture integration to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserConnecture(userId: number, id: number) {
    return this.sendRequest(`/user/${userId}/connecture/${id}`, null, { method: 'DELETE' });
  }

  /**
   * Add new Connecture integration for user
   *
   * Creates a new Connecture integration configuration for the specified user.
   *
   * @param userId - The ID of the user to add Connecture integration for
   * @param payload - The Connecture configuration data
   * @returns Promise containing the created integration details
   */
  public addUserConnecture(userId: number, payload: object) {
    return this.sendRequest(`/user/${userId}/connecture`, null, { method: 'POST', body: payload });
  }

  /**
   * Create Connecture session for user
   *
   * Initiates a new session with the Connecture integration service.
   * Used for authenticated access to Connecture APIs and services.
   *
   * @param userId - The ID of the user to create the session for
   * @param options - Session configuration options
   * @returns Promise containing the session details and authentication tokens
   */
  public createConnectureSession(userId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/integrations/connecture/create-session`, options);
  }

  /**
   * Create SunFire session for user
   *
   * Initiates a new session with the SunFire integration service.
   * Used for authenticated access to SunFire APIs and enrollment tools.
   *
   * @param userId - The ID of the user to create the session for
   * @param options - Session configuration options
   * @returns Promise containing the session details and authentication tokens
   */
  public createSunFireSession(userId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/integrations/sunfire/create-session`, options);
  }

  /**
   * Get SunFire customer code for user
   *
   * Retrieves the customer code associated with a user's SunFire integration.
   * Used for identifying the user within the SunFire system.
   *
   * @param userId - The ID of the user whose customer code to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the SunFire customer code
   */
  public getSunFireCustomerCode(userId: number, options: object = {}) {
    return this.sendRequest(`/user/${userId}/integrations/sunfire/customer-code`, options);
  }

  /**
   * Create SunFire prospect post
   *
   * Create a prospect on SunFire.
   *
   * @param reference - The reference of the SunFire org
   * @param options - Additional query parameters
   * @returns Promise containing the SunFire prospect post data
   */
  public createSunFireProspectPost(reference: number, payload: object = {}) {
    return this.sendRequest(`/integrations/sunfire/${reference}/prospect-post`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Subscribe to VoIP webhooks
   *
   * Registers webhook endpoints for VoIP provider notifications and events.
   * Enables real-time call tracking and integration features.
   *
   * @param providerReference - The reference identifier of the VoIP provider
   * @param payload - Webhook configuration including endpoint URLs and event types
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the subscription confirmation
   */
  public subscribeUserVoipWebhooks(
    providerReference: string,
    payload: object = {},
    method = 'oauth'
  ) {
    return this.sendRequest(`/integrations/${method}/${providerReference}/webhooks`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Unsubscribe from VoIP webhooks
   * 
   * Removes webhook endpoint registrations for a VoIP provider.
   * Stops receiving real-time notifications and events.
   * 
   * @param providerReference - The reference identifier of the VoIP provider
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the unsubscription confirmation
   */
  public unsubscribeUserVoipWebhooks(providerReference: string, method = 'oauth', payload: object = {}) {
    return this.sendRequest('/integrations/' + method + '/' + providerReference + '/webhooks', null, { method: 'DELETE', body: payload });
  }

  /**
   * Send conference through provider
   *
   * Sends conference using an integrated provider service.
   *
   * @param providerReference - The reference identifier of the provider
   * @param payload - conference data
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the conference send confirmation
   */
  public sendUserIntegrationProviderConference(
    providerReference: string,
    payload: object = {},
    method = 'oauth'
  ) {
    return this.sendRequest(`/integrations/${method}/${providerReference}/conference`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Send SMS through VoIP provider
   *
   * Sends SMS messages using an integrated VoIP provider's messaging service.
   *
   * @param providerReference - The reference identifier of the VoIP provider
   * @param payload - SMS data including recipient, message content, and sender information
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the SMS send confirmation and message ID
   */
  public sendUserVoipSms(providerReference: string, payload: object = {}, method = 'oauth') {
    return this.sendRequest(`/integrations/${method}/${providerReference}/sms/send`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Subscribe to VoIP SMS webhooks
   *
   * Registers webhook endpoints for SMS notifications and delivery receipts from VoIP provider.
   * Enables real-time SMS tracking and automated responses.
   *
   * @param providerReference - The reference identifier of the VoIP provider
   * @param payload - Webhook configuration for SMS events
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the SMS webhook subscription confirmation
   */
  public subscribeUserVoipSmsWebhooks(
    providerReference: string,
    payload: object = {},
    method = 'oauth'
  ) {
    return this.sendRequest(`/integrations/${method}/${providerReference}/webhooks`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Unsubscribe from VoIP SMS webhooks
   *
   * Removes SMS webhook endpoint registrations for a VoIP provider.
   * Stops receiving SMS notifications and delivery receipts.
   *
   * @param providerReference - The reference identifier of the VoIP provider
   * @param method - Authentication method (default: 'oauth')
   * @returns Promise containing the SMS webhook unsubscription confirmation
   */
  public unsubscribeUserVoipSmsWebhooks(providerReference: string, method = 'oauth') {
    return this.sendRequest(`/integrations/${method}/${providerReference}/webhooks`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Get user notification preferences
   *
   * Retrieves all notification preferences and settings for a specific user.
   *
   * @param userId - The ID of the user whose preferences to retrieve
   * @param options - Additional query parameters for filtering
   * @returns Promise containing the user's notification preferences
   */
  public getUserNotificationPreferences(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/notification-preferences`, options);
  }

  /**
   * Get user activity logs
   *
   * Retrieves activity logs and audit trail for a specific user.
   *
   * @param userId - The ID of the user whose logs to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's activity logs
   */
  public getUserLogs(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/logs`, options);
  }

  /**
   * Create user activity log entry
   *
   * Creates a new activity log entry for tracking user actions and system events.
   *
   * @param userId - The ID of the user to create the log entry for
   * @param payload - Log entry data including action type, details, and timestamp
   * @returns Promise containing the created log entry
   */
  public createUserLog(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/logs`, null, { method: 'POST', body: payload });
  }

  /**
   * Update user activity log entry
   *
   * Modifies an existing user activity log entry.
   *
   * @param userId - The ID of the user who owns the log entry
   * @param userLogId - The ID of the log entry to update
   * @param payload - Updated log entry data
   * @returns Promise containing the updated log entry
   */
  public updateUserLog(userId: number, userLogId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/logs/${userLogId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Remove user activity log entry
   *
   * Permanently deletes a user activity log entry.
   *
   * @param userId - The ID of the user who owns the log entry
   * @param userLogId - The ID of the log entry to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserLog(userId: number, userLogId: number) {
    return this.sendRequest(`/users/${userId}/logs/${userLogId}`, null, { method: 'DELETE' });
  }

  /**
   * Add user notification preference
   *
   * Creates a new notification preference setting for a user.
   *
   * @param userId - The ID of the user to add the preference for
   * @param payload - Notification preference configuration data
   * @returns Promise containing the created notification preference
   */
  public addUserNotificationPreference(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/notification-preferences`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Update user notification preference
   *
   * Modifies an existing notification preference setting for a user.
   *
   * @param userId - The ID of the user who owns the preference
   * @param notificationPreferenceId - The ID of the notification preference to update
   * @param payload - Updated notification preference data
   * @returns Promise containing the updated notification preference
   */
  public updateUserNotificationPreference(
    userId: number,
    notificationPreferenceId: number,
    payload: any[]
  ) {
    return this.sendRequest(
      `/users/${userId}/notification-preferences/${notificationPreferenceId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Delete user notification preference
   *
   * Permanently removes a notification preference setting from a user's account.
   *
   * @param userId - The ID of the user who owns the preference
   * @param notificationPreferenceId - The ID of the notification preference to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserNotificationPreference(userId: number, notificationPreferenceId: number) {
    return this.sendRequest(
      `/users/${userId}/notification-preferences/${notificationPreferenceId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Get user offices
   *
   * Retrieves all office locations associated with a specific user.
   *
   * @param userId - The ID of the user whose offices to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's office locations
   */
  public getUserOffices(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/offices`, options);
  }

  /**
   * Add user office
   *
   * Creates a new office location for a user.
   *
   * @param userId - The ID of the user to add the office for
   * @param payload - Office location data including address and contact information
   * @returns Promise containing the created office details
   */
  public addUserOffice(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/offices`, null, { method: 'POST', body: payload });
  }

  /**
   * Update user office
   *
   * Modifies an existing office location for a user.
   *
   * @param userId - The ID of the user who owns the office
   * @param officeId - The ID of the office to update
   * @param payload - Updated office data
   * @returns Promise containing the updated office details
   */
  public updateUserOffice(userId: number, officeId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/offices/${officeId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete user office
   *
   * Permanently removes an office location from a user's account.
   *
   * @param userId - The ID of the user who owns the office
   * @param officeId - The ID of the office to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserOffice(userId: number, officeId: number) {
    return this.sendRequest(`/users/${userId}/offices/${officeId}`, null, { method: 'DELETE' });
  }

  /**
   * Get user client portals
   *
   * Retrieves all client portal configurations for a specific user.
   *
   * @param userId - The ID of the user whose client portals to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the user's client portal configurations
   */
  public getUserClientPortals(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/client-portals`, options);
  }

  /**
   * Get specific user client portal
   *
   * Retrieves details for a single client portal configuration.
   *
   * @param userClientPortalId - The ID of the client portal to retrieve
   * @param options - Additional query parameters
   * @returns Promise containing the client portal details
   */
  public getUserClientPortal(userClientPortalId: number, options: object = {}) {
    return this.sendRequest(`/users/client-portals/${userClientPortalId}`, options);
  }

  /**
   * Update user client portal
   *
   * Modifies an existing client portal configuration.
   *
   * @param userId - The ID of the user who owns the client portal
   * @param clientPortalId - The ID of the client portal to update
   * @param payload - Updated client portal configuration data
   * @returns Promise containing the updated client portal details
   */
  public updateUserClientPortal(userId: number, clientPortalId: number, payload: any[]) {
    return this.sendRequest(`/users/${userId}/client-portals/${clientPortalId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete user client portal
   *
   * Permanently removes a client portal configuration from a user's account.
   *
   * @param userId - The ID of the user who owns the client portal
   * @param clientPortalId - The ID of the client portal to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserClientPortal(userId: number, clientPortalId: number) {
    return this.sendRequest(`/users/${userId}/client-portals/${clientPortalId}`, null, {
      method: 'DELETE',
    });
  }

  /**
   * Add user client portal
   *
   * Creates a new client portal configuration for a user.
   *
   * @param userId - The ID of the user to create the client portal for
   * @param payload - Client portal configuration data
   * @returns Promise containing the created client portal details
   */
  public addUserClientPortal(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/client-portals`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get user calendar providers
   *
   * Retrieves all calendar integration providers available to a user.
   *
   * @param userId - The ID of the user whose calendar providers to retrieve
   * @returns Promise containing the list of available calendar providers
   */
  public getUserCalendarProviders(userId: number) {
    return this.sendRequest(`/users/${userId}/calendar-providers`);
  }

  /**
   * Get specific user calendar provider
   *
   * Retrieves details for a specific calendar provider integration.
   *
   * @param userId - The ID of the user who owns the calendar provider
   * @param providerReference - The reference identifier of the calendar provider
   * @returns Promise containing the calendar provider details
   */
  public getUserCalendarProvider(userId: number, providerReference: string) {
    return this.sendRequest(`/users/${userId}/calendar-providers/${providerReference}`);
  }

  /**
   * Get user blitz campaigns
   *
   * Retrieves all blitz marketing campaigns configured in the system.
   *
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the list of blitz campaigns
   */
  public getUserBlitzs(options: object = {}) {
    return this.sendRequest('/users/blitz', options);
  }

  /**
   * Add user blitz campaign
   *
   * Creates a new blitz marketing campaign.
   *
   * @param payload - Blitz campaign configuration data
   * @returns Promise containing the created blitz campaign details
   */
  public addUserBlitz(payload) {
    return this.sendRequest('/users/blitz', null, { method: 'POST', body: payload });
  }

  /**
   * Remove user blitz campaign
   *
   * Permanently deletes a blitz marketing campaign.
   *
   * @param blitzId - The ID of the blitz campaign to remove
   * @returns Promise containing the deletion confirmation
   */
  public removeUserBlitz(blitzId: number) {
    return this.sendRequest(`/users/blitz/${blitzId}`, null, { method: 'DELETE' });
  }

  /**
   * Get user app webhooks
   *
   * Retrieves all webhook configurations for a specific app integration.
   *
   * @param reference - The reference identifier of the app
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the list of app webhooks
   */
  public getUserAppWebhooks(reference: string, options: object = {}) {
    return this.sendRequest(`/apps/${reference}/webhooks`, options);
  }

  /**
   * Update user app webhook
   *
   * Modifies an existing app webhook configuration.
   *
   * @param appWebhookId - The ID of the app webhook to update
   * @param payload - Updated webhook configuration data
   * @returns Promise containing the updated webhook details
   */
  public updateUserAppWebhook(appWebhookId: number, payload: object) {
    return this.sendRequest(`/apps/webhooks/${appWebhookId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Delete user app webhook
   *
   * Permanently removes an app webhook configuration.
   *
   * @param appWebhookId - The ID of the app webhook to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserAppWebhook(appWebhookId: number) {
    return this.sendRequest(`/apps/webhooks/${appWebhookId}`, null, { method: 'DELETE' });
  }

  /**
   * Create user app webhook
   *
   * Creates a new webhook configuration for an app integration.
   *
   * @param reference - The reference identifier of the app
   * @param payload - Webhook configuration data
   * @returns Promise containing the created webhook details
   */
  public createUserAppWebhook(reference: string, payload: object) {
    return this.sendRequest(`/apps/${reference}/webhooks`, null, { method: 'POST', body: payload });
  }

  /**
   * Get agency notification preferences
   *
   * Retrieves all notification preferences configured at the agency level.
   *
   * @param agencyId - The ID of the agency whose preferences to retrieve
   * @param options - Query parameters for filtering and pagination
   * @returns Promise containing the agency's notification preferences
   */
  public getAgencyNotificationPreferences(agencyId: number, options: object = {}) {
    return this.sendRequest(`/agencies/${agencyId}/notification-preferences`, options);
  }

  /**
   * Update agency notification preference
   *
   * Modifies an existing notification preference at the agency level.
   *
   * @param agencyId - The ID of the agency that owns the preference
   * @param notificationPreferenceId - The ID of the notification preference to update
   * @param payload - Updated notification preference data
   * @returns Promise containing the updated notification preference
   */
  public updateAgencyNotificationPreference(
    agencyId: number,
    notificationPreferenceId: number,
    payload: any[]
  ) {
    return this.sendRequest(
      `/agencies/${agencyId}/notification-preferences/${notificationPreferenceId}`,
      null,
      { method: 'PATCH', body: payload }
    );
  }

  /**
   * Delete agency notification preference
   *
   * Permanently removes a notification preference from an agency's configuration.
   *
   * @param agencyId - The ID of the agency that owns the preference
   * @param notificationPreferenceId - The ID of the notification preference to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteAgencyNotificationPreference(agencyId: number, notificationPreferenceId: number) {
    return this.sendRequest(
      `/agencies/${agencyId}/notification-preferences/${notificationPreferenceId}`,
      null,
      { method: 'DELETE' }
    );
  }

  /**
   * Add agency notification preference
   *
   * Creates a new notification preference at the agency level.
   *
   * @param agencyId - The ID of the agency to add the preference for
   * @param payload - Notification preference configuration data
   * @returns Promise containing the created notification preference
   */
  public addAgencyNotificationPreference(agencyId: number, payload: object) {
    return this.sendRequest(`/agencies/${agencyId}/notification-preferences`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Get agency report
   *
   * Retrieves a specific report for an agency with optional filtering and parameters.
   *
   * @param userId - The ID of the user requesting the report
   * @param agencyId - The ID of the agency the report belongs to
   * @param reportName - The name/type of the report to retrieve
   * @param options - Query parameters for filtering and customization
   * @returns Promise containing the report data
   */
  public getReport(userId: number, agencyId: number, reportName: string, options: object = {}) {
    return this.sendRequest(`/users/${userId}/agency/${agencyId}/reports/${reportName}`, options);
  }

  /**
   * Export agency report
   *
   * Generates and downloads a report export in the specified format (Excel, PDF, etc.).
   *
   * @param userId - The ID of the user requesting the export
   * @param agencyId - The ID of the agency the report belongs to
   * @param reportName - The name/type of the report to export
   * @param options - Export options including format and filtering
   * @returns Promise containing the exported report file blob
   */
  public getReportExport(
    userId: number,
    agencyId: number,
    reportName: string,
    options: object = {}
  ) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/reports/${reportName}/export`,
      options,
      { responseType: 'blob' }
    );
  }

  /**
   * Get report filter options
   *
   * Retrieves available filter options for a specific report type.
   * Used to populate filter dropdowns and options in the UI.
   *
   * @param userId - The ID of the user requesting the report
   * @param agencyId - The ID of the agency the report belongs to
   * @param reportName - The name/type of the report
   * @returns Promise containing the available filter options
   */
  public getReportFilterOptions(userId: number, agencyId: number, reportName: string) {
    return this.sendRequest(
      `/users/${userId}/agency/${agencyId}/reports/${reportName}/options`,
      null
    );
  }
  public addIndFlowAssignment(individualId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${individualId}/flow-assignments`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public addIndFlowAssignmentBulk(payload: object = {}) {
    return this.sendRequest('/individuals/flow-assignments/bulk', null, {
      method: 'POST',
      body: payload,
    });
  }
  public getIndFlowAssignments(individualId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${individualId}/flow-assignments`, options);
  }
  public getIndFlowAssignment(individualId: number, assignmentId: number, options: object = {}) {
    return this.sendRequest(
      `/individuals/${individualId}/flow-assignments/${assignmentId}`,
      options
    );
  }
  public removeIndFlowAssignment(individualId: number, assignmentId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${individualId}/flow-assignments/${assignmentId}`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public moveIndFlowAssignment(individualId: number, payload: object = {}) {
    return this.sendRequest(`/individuals/${individualId}/flow-assignments/move`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public getUserFlows(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/flows`, options);
  }
  public getUserFlow(flowId: number, options: object = {}) {
    return this.sendRequest(`/users/flows${flowId}`, options);
  }
  public updateUserFlow(flowId: number, payload: any = {}) {
    return this.sendRequest(`/users/flows/${flowId}`, null, { method: 'PATCH', body: payload });
  }
  public removeUserFlow(flowId: number) {
    return this.sendRequest(`/users/flows/${flowId}`, null, { method: 'DELETE' });
  }
  public addUserFlow(payload) {
    return this.sendRequest('/users/flows', null, { method: 'POST', body: payload });
  }
  public saveFlowBundle(flowId: number, payload: any) {
    return this.sendRequest(`/users/flows/${flowId}/bundle`, null, {
      method: 'PUT',
      body: payload,
    });
  }
  public getUserFlowStages(flowId: number, options: object = {}) {
    return this.sendRequest(`/users/flows/${flowId}/stages`, options);
  }
  public getUserFlowStageInds(flowId: number, flowStageId: number, options: object = {}) {
    return this.sendRequest(`/users/flows/${flowId}/stages/${flowStageId}/individuals`, options);
  }
  public getUserFlowStage(flowId: number, flowStageId: number, options: object = {}) {
    return this.sendRequest(`/users/flows/${flowId}/stages${flowStageId}`, options);
  }
  public getIndFlowHistory(indId: number, options: object = {}) {
    return this.sendRequest(`/individuals/${indId}/flow-assignments/history`, options);
  }
  public updateUserFlowStage(flowId: number, flowStageId: number, payload: any = {}) {
    return this.sendRequest(`/users/flows/${flowId}/stages/${flowStageId}`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public updateUserFlowStageSortOrder(flowId: number, payload: any = {}) {
    return this.sendRequest(`/users/flows/${flowId}/stages/sort-order`, null, {
      method: 'PATCH',
      body: payload,
    });
  }
  public removeUserFlowStage(flowId: number, flowStageId: number) {
    return this.sendRequest(`/users/flows/${flowId}/stages/${flowStageId}`, null, {
      method: 'DELETE',
    });
  }
  public addUserFlowStage(flowId: number, payload) {
    return this.sendRequest(`/users/flows/${flowId}/stages`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public moveAllInStage(flowId: number, payload) {
    return this.sendRequest(`/flows/${flowId}/assignments/move-all`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public closeAllInStage(flowId: number, payload) {
    return this.sendRequest(`/flows/${flowId}/assignments/close-all`, null, {
      method: 'POST',
      body: payload,
    });
  }
  public removeAllInStage(flowId: number, flowStageId: number) {
    return this.sendRequest(`/users/flows/${flowId}/stages/${flowStageId}/assignments`, null, {
      method: 'DELETE',
    });
  }
  public closeIndFlowAssignment(indId: number, assignmentId: number, payload: any = {}) {
    return this.sendRequest(`/individuals/${indId}/flow-assignments/${assignmentId}/close`, null, {
      method: 'PATCH',
      body: payload,
    });
  }

  /**
   * Get a sample individual record
   *
   * Retrieves a sample individual record for testing, demonstration,
   * or template purposes. Useful for form examples and data structure reference.
   *
   * @returns Promise containing a sample individual record
   */
  public getSampleInd() {
    return this.sendRequest('/utilities/sample-individual', null);
  }

  /**
   * Send a chat message to OpenAI integration
   *
   * Sends a message to the OpenAI chat integration and receives an AI-generated response.
   * Used for AI-powered features like content generation and assistance.
   *
   * @param payload - The chat message payload including conversation context
   * @returns Promise containing the AI response
   */
  public getOpenAIChat(payload: object = {}) {
    return this.sendRequest('/integrations/openai/chat', null, { method: 'POST', body: payload });
  }

  /**
   * Generate download URL for agency report upload results
   *
   * Creates a download URL for the results of an agency report upload processing.
   * Includes authentication tokens for secure access to the processed data.
   *
   * @param userId - The ID of the user requesting the download
   * @param agencyId - The ID of the agency that owns the report
   * @param reportId - The ID of the processed report upload
   * @param options - Additional download options
   * @returns String URL for downloading the report results
   */
  public downloadAgencyReportUploadResults(
    userId: number,
    agencyId: number,
    reportId: number,
    options: object = {}
  ) {
    if (this.accessToken) {
      options['accessToken'] = this.accessToken;
    }
    return `${this.baseUrl}/users/${userId}/agency/${agencyId}/report-uploads/${reportId}/download-results?${this.buildQuery(options)}`;
  }

  /**
   * Health Sherpa Medicare Enroll
   *
   * Creates or updates a contact at HealthSherpa
   *
   * @param userId - The ID of the user who will own this individual record
   * @param payload - The individual data to create
   * @returns Promise containing the created individual's data
   */
  public healthSherpaMedicareEnroll(userId: number, payload: object = {}) {
    return this.sendRequest(`/users/${userId}/integrations/pat/healthsherpamedicare/enroll`, null, {
      method: 'POST',
      body: payload,
    });
  }

  /**
   * Gets users email activity send through SendGrid
   *
   * @param params - Additional criteria for search
   * @returns Promise containing messages
   */
  public getUserEmailActivity(options: object = {}) {
    return this.sendRequest('/users/email-activity/', options);
  }

  /**
   * Get user IPs
   *
   * @param userId - The ID of the user to get the IPs for
   * @returns Promise containing the IPs
   */
  public getUserIps(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/ips`, options);
  }

  /**
   * Add user IP
   *
   * Creates a new IP for a user.
   *
   * @param userId - The ID of the user to add the IP for
   * @param payload - IP configuration data
   * @returns Promise containing the created IP
   */
  public addUserIp(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/ips`, null, { method: 'POST', body: payload });
  }

  /**
   * Delete user IP
   *
   * Deleted an IP for a user.
   *
   * @param userId - The ID of the user to add the IP for
   * @param ipId - The ID of the IP to delete
   * @param payload - IP configuration data
   * @returns Promise
   */
  public removeUserIp(userId: number, ipId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/ips/${ipId}`, null, {
      method: 'DELETE',
      body: payload,
    });
  }

  /**
   * Get staff IPs
   *
   * @param staffId - The ID of the staff to get the IPs for
   * @returns Promise containing the IPs
   */
  public getStaffIps(staffId: number, options: object = {}) {
    return this.sendRequest(`/staff/${staffId}/ips`, options);
  }

  /**
   * Add staff IP
   *
   * Creates a new IP for a staff.
   *
   * @param staffId - The ID of the staff to add the IP for
   * @param payload - IP configuration data
   * @returns Promise containing the created IP
   */
  public addStaffIp(staffId: number, payload: object) {
    return this.sendRequest(`/staff/${staffId}/ips`, null, { method: 'POST', body: payload });
  }

  /**
   * Delete staff IP
   *
   * Deleted an IP for a staff.
   *
   * @param staffId - The ID of the staff to add the IP for
   * @param ipId - The ID of the IP to delete
   * @param payload - IP configuration data
   * @returns Promise
   */
  public removeStaffIp(staffId: number, ipId: number, payload: object) {
    return this.sendRequest(`/staff/${staffId}/ips/${ipId}`, null, {
      method: 'DELETE',
      body: payload,
    });
  }

  /**
   * Get all API tokens for a user
   *
   * @param userId - The ID of the user to get API tokens for
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing the list of API tokens
   */
  public getUserApiTokens(userId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/api-tokens`, options);
  }

  /**
   * Get a single API token for a user
   *
   * @param userId - The ID of the user
   * @param tokenId - The ID of the API token to retrieve
   * @param options - Query parameters to be sent with this request
   * @returns Promise containing the API token
   */
  public getUserApiToken(userId: number, tokenId: number, options: object = {}) {
    return this.sendRequest(`/users/${userId}/api-tokens/${tokenId}`, options);
  }

  /**
   * Create a new API token for a user
   *
   * @param userId - The ID of the user to create the API token for
   * @param payload - The API token data
   * @returns Promise containing the created API token
   */
  public createUserApiToken(userId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/api-tokens`, null, { method: 'POST', body: payload });
  }

  /**
   * Update an existing API token for a user
   *
   * @param userId - The ID of the user
   * @param tokenId - The ID of the API token to update
   * @param payload - The updated API token data
   * @returns Promise containing the updated API token
   */
  public updateUserApiToken(userId: number, tokenId: number, payload: object) {
    return this.sendRequest(`/users/${userId}/api-tokens/${tokenId}`, null, {
      method: 'PUT',
      body: payload,
    });
  }

  /**
   * Delete an API token for a user
   *
   * @param userId - The ID of the user
   * @param tokenId - The ID of the API token to delete
   * @returns Promise containing the deletion confirmation
   */
  public deleteUserApiToken(userId: number, tokenId: number) {
    return this.sendRequest(`/users/${userId}/api-tokens/${tokenId}`, null, { method: 'DELETE' });
  }
}
